import { Location } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  viewChild,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ActivatedRoute,
  DefaultUrlSerializer,
  NavigationStart,
  ParamMap,
  Params,
  Route,
  Router,
  UrlSegment,
  UrlSegmentGroup,
  UrlTree,
} from '@angular/router';
import { PropertiesService, PropertyViewModel } from '@barthauer/abc/common-editors';
import {
  AbcCore,
  CompareHelper,
  ConvertHelper,
  DefinitionDocumentReference,
  DotNetBridge,
  NavigationInfo,
  ObjectHelper,
  ProfileInfoViewModel,
  Routing,
  StringHelper,
} from '@barthauer/abc/core';
import { CompressionService } from '@barthauer/abc/core/assets-manager';
import { ProfileInfo, ProfileService, UserPersonInfoService, UserService } from '@barthauer/abc/core/authentication';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import {
  CacheStorageAdapter,
  ICoreDataEntity,
  ObjectTypeInfoViewModel,
  PersistenceService,
  PersistMode,
  QueryHelper,
  QueryObjectsModel,
  ReadDocumentModel,
} from '@barthauer/abc/core/data';
import { catchErrorAndRethrow } from '@barthauer/abc/core/error-handling';
import { ResourceManager } from '@barthauer/abc/core/resources';
import { BceService, GroupPacket } from '@barthauer/abc/management/configuration-explorer';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { ParameterHelper, ParameterValue } from '@barthauer/abc/queries/parameters/core';
import {
  _AbcDrawerComponent,
  _AbcSideNavComponent,
  AbcContainerService,
  AbcCoreComponentService,
  AbcHtmlEditorComponent,
  AbcNotificationComponent,
  AbcPopupComponent,
  AbcSplitterV2Component,
  AbcTileNav,
  AbcWindowComponent,
  INotification,
  IWindow,
  OnTileClickEvent,
  ToastType,
  WindowStyle,
} from '@barthauer/abc/ui-components';
import { FocusedRowChangedEvent, FocusedRowChangingEvent } from 'devextreme/ui/data_grid';
import { ItemClickEvent } from 'devextreme/ui/drop_down_button';
import { ResizeEvent } from 'devextreme/ui/resizable';
import { ItemCollapsedEvent, ItemExpandedEvent } from 'devextreme/ui/splitter';
import 'hammerjs';
import { BCEDocumentType } from 'libs/abc/management/configuration-explorer/src/lib/models/bce.model';
import { ParameterService } from 'libs/abc/queries/parameters/values-editor/src/lib/services/parameter.service';
import { lastValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { concatMap, map, takeUntil } from 'rxjs/operators';
import {
  AbcArenaService,
  ArenaComponentActions,
  ArenaContext,
  ArenaDataContext,
  ArenaDefaultGroupMode,
  ArenaLayoutContext,
  ArenaUIConfiguration,
  ArenaUIParams,
  Interfaces,
  IWindowAdapter,
  LayoutUsage,
  NavStateItem,
  ParameterNames,
  Status,
} from '../abc-arena.service';
import { ComponentRegister, ComponentRegistryEntry } from '../helpers/component-register';
import { ArenaPluginManager, IArenaLayoutPluginActions } from '../plugins/arena-plugin-manager';
import { IArenaPlugin } from '../plugins/arena-plugin.model';
import { DataService } from '../services/data.service';
import { ArenaService, Notification } from './arena.service';
import { DataFormComponent } from './data-form/data-form.component';
import { HignaComponent } from './higna/higna.component';
import { HigonComponent } from './higon/higon.component';
import { AdditionalHigonAction, TargetLayoutInfo } from './higon/higon.model';
import { MenuComponent } from './menu/menu.component';
import { ArenaUser } from './models/data.model';
import { SpecialType } from './models/layout.model';
import { NavigationComponent } from './navigation/navigation.component';
import { Command } from './navigation/navigation.model';
import {
  NavigationGroupViewModel,
  NavigationLayoutViewModel,
  NavigationNodeBaseViewModel,
  NavigationViewModel,
} from './navigation/navigation.viewModel';
import { SettingsComponent } from './settings/settings.component';
import { MenuButton } from './shared/type';
import { LayoutColumnViewModel, LayoutViewModel } from './viewModels/layout.viewModel';
import { IArenaSettings } from '../models/arena-settings.model';
import { ArenaSettingsService } from '../services/arena-settings.service';
import { AppSettingsService } from '../services/app-settings.service';
import { DataFormSettingsService } from '../services/dataform-settings.service';
import { ThemeService } from '../services/theme.service';

export enum ArenaBceDocumentType {
  Report = 'Report',
  Query = 'Query',
  DmlQuery = 'DmlQuery',
}

export interface IArenaLayout {
  removeGridObjectById(id: string): Promise<number>;
  getCurrentOrFilteredDataEntities(allowMultiSelect?: boolean): Promise<ICoreDataEntity[]>;
  getFilteredDataEntities(): Promise<ICoreDataEntity[]>;
  beginLoading(): { active: boolean };
  endLoading(token: { active: boolean }, noChangeDetection?: boolean): void;
  requery(): Promise<void>;
}

interface PendingCommentEditor {
  key: string;
  value: string;
}

@Component({
  selector: 'arena-container',
  templateUrl: './arena.component.html',
  styleUrls: ['./arena.component.scss'],
  standalone: false,
})
export class ArenaComponent implements OnInit, OnDestroy, AfterViewChecked, IWindowAdapter {
  @ViewChild('abcNotification', { static: true, read: ViewContainerRef })
  abcNotification: ViewContainerRef;
  @ViewChild('abcWindowContainer', { read: ViewContainerRef })
  abcWindow: ViewContainerRef;
  @ViewChild('sideNav') abcSideNav: _AbcSideNavComponent;
  @ViewChild('abcDrawer') abcDrawer: _AbcDrawerComponent;
  @ViewChild('dividerDrag') dividerDrag: ElementRef;
  @ViewChild('library', { static: true, read: ViewContainerRef })
  abcLibrary: ViewContainerRef;
  @ViewChild('navigationComponent') navigationComponent: NavigationComponent;
  @ViewChild('layoutSplitterComponent')
  layoutSplitterComponent: AbcSplitterV2Component;
  @ViewChild('menuComponent') menuComponent: MenuComponent;
  @ViewChild('dataFormComponent') dataFormComponent: DataFormComponent;
  @ViewChild('hignaComponent') hignaComponent: HignaComponent;
  @ViewChild('higonComponent') higonComponent: HigonComponent;
  @ViewChild('arenaProfilesPopup') arenaProfilesPopup: AbcPopupComponent;

  private sideNavItemsContainer = viewChild<ElementRef<HTMLElement>>('sideNavItemsContainer');
  private _context: ArenaContext = new ArenaContext();

  public readonly queryParamsStateChanged: EventEmitter<ParamMap> = new EventEmitter<ParamMap>();
  public sideNavWidth: number;
  public sideNavIsResized: boolean = false;
  public selectedArenaProfile: string = localStorage.getItem('ArenaSettings_PublicName') || null;

  @Input() get context(): ArenaContext {
    return this._context;
  }
  set context(value: ArenaContext) {
    ObjectHelper.mergeDeepInto(this._context, value);
  }

  public get currentLayoutContext(): ArenaLayoutContext {
    let context: ArenaLayoutContext;
    if (this.tileNavs?.length) {
      context = this.tileNavs[this.tileNavs.length - 1]?.data;
    }
    return context;
  }

  public get currentDataContext(): ArenaDataContext {
    return this.context.nextDataContext ?? this.currentLayoutContext?.data;
  }

  public get layoutPath(): string {
    return this.currentLayoutContext?.layoutPath;
  }

  private _uiconfig: ArenaUIConfiguration = new ArenaUIConfiguration();
  @Input() get uiconfig(): ArenaUIConfiguration {
    this._uiconfig.level = this.tileNavs.length;
    return this._uiconfig;
  }
  set uiconfig(value: ArenaUIConfiguration) {
    ObjectHelper.mergeDeepInto(this._uiconfig, value);
  }

  private _pluginManager: ArenaPluginManager;
  private get pluginManager(): ArenaPluginManager {
    if (!this._pluginManager) {
      this._pluginManager = new ArenaPluginManager(this.abcCore, () => this.isBusy && this.isBusy());
    }
    return this._pluginManager;
  }
  @Input() get plugins(): IArenaPlugin[] {
    return this.pluginManager.plugins;
  }
  set plugins(value: IArenaPlugin[]) {
    this.pluginManager.plugins = value;
  }

  @Output() onObjectsSelected: EventEmitter<ICoreDataEntity[]> = new EventEmitter();
  @HostListener('window:resize', ['$event'])
  public res = ResourceManager.getResources('abc-arena');
  private nvDocument_: NavigationViewModel;
  private async getNavigation(): Promise<NavigationViewModel> {
    if (!this.nvDocument_) {
      await this.abcArenaService.actions.loadArenaNavigation(
        await this.arenaService.getNavigation(this.arenaService.arenaName),
        true,
      );
    }
    return this.nvDocument_;
  }
  public nvGroup: NavigationGroupViewModel[] = [];
  public nestedNvGroup: NavigationGroupViewModel[] = [];
  private onNotifyChanges: (hasAnyChanges: boolean) => Promise<void> = async (_) => {};
  public tileNavs: AbcTileNav[] = [];
  private origActions: ArenaComponentActions;
  private lastObjectId: string;
  private currentDataEntity: ICoreDataEntity;
  private customComponent: {
    componentRef: ComponentRef<unknown>;
    domElement: HTMLElement;
  };
  private currentLayoutPluginActions: IArenaLayoutPluginActions;

  public get isStandaloneDataForm(): boolean {
    return (
      !this.el?.nativeElement?.parentNode?.parentNode?.parentNode?.localName?.includes('root') ||
      this.uiconfig.isStandaloneDataForm
    );
  }

  private get currentNavigationNode(): NavigationNodeBaseViewModel {
    return this.navigationComponent?.currentNode ?? this.currentNavigationGroup;
  }
  private currentNode: NavigationNodeBaseViewModel;

  public get layoutViewModel(): LayoutViewModel {
    return this.currentLayoutContext?.layout;
  }

  public abcWindowCpt: any;
  private destroyApp$ = new Subject<void>();
  public startFirstPos: number;
  public isInitialized = true;
  private arenaComponentLoadedSubject = new Subject<boolean>();
  public arenaComponentLoaded$ = this.arenaComponentLoadedSubject.asObservable();
  public profileInfoObservable$: Observable<ProfileInfo>;
  public arenaUserObservable$: Observable<ArenaUser>;
  public showLoader = true;
  public windowInstance: AbcWindowComponent;
  public currentNavigationGroup: NavigationGroupViewModel;
  public rtlEnabled: boolean;
  public canLogout: boolean;
  public allowSettings: boolean;
  public screenHeight: number;
  private isReadOnly: boolean;
  private resizeObserver: ResizeObserver;
  public beforeLogoutFunc: () => void;
  public currentProfileDisplayText: string;
  public arenaProfiles: string[];
  public isSavingArenaProfile: boolean;

  constructor(
    public arenaService: ArenaService,
    private abcArenaService: AbcArenaService,
    private abcContainer: AbcContainerService,
    private userService: UserService,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private abcCore: AbcCore,
    private profileService: ProfileService,
    private changeDetectorRef: ChangeDetectorRef,
    private pdcPropertiesService: PropertiesService,
    private userPersonInfoService: UserPersonInfoService,
    private persistenceService: PersistenceService,
    private messageBoxService: MessageBoxService,
    private bceService: BceService,
    private el: ElementRef,
    private parameterService: ParameterService,
    private dataService: DataService,
    private abcConfigService: AbcConfigService,
    private compression: CompressionService,
    private arenaSettingsService: ArenaSettingsService,
    private appSettingsService: AppSettingsService,
    private iconRegistry: MatIconRegistry,
    private dataFormSettingsService: DataFormSettingsService,
    private themeService: ThemeService,
    private abcCoreComponentService: AbcCoreComponentService,
    private sanitizer: DomSanitizer,
  ) {
    this.registerIcons(iconRegistry, sanitizer);
    this.applyPersistedStyleSettings();

    this.loadArenaUser();
    this.configureArenaService();
    this.initializeArenaServiceActions();
    this.applyDataFormLayoutSettingsFromSettings();

    this.rtlEnabled = arenaService.rtlEnabled;

    this.getIfSettingsAllowed().then((yes) => (this.allowSettings = yes));
  }

  private registerIcons(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
    iconRegistry.addSvgIcon(
      'basysArenaIcon',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/BaSYS-opera-manager.svg'),
    );
    iconRegistry.addSvgIcon('editor-menu', sanitizer.bypassSecurityTrustResourceUrl('assets/images/editor-menu.svg'));
    iconRegistry.addSvgIcon(
      'properties_db',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/properties_db.svg'),
    );
    iconRegistry.addSvgIcon('info_db', sanitizer.bypassSecurityTrustResourceUrl('assets/images/info_db.svg'));
    iconRegistry.addSvgIcon('documents_db', sanitizer.bypassSecurityTrustResourceUrl('assets/images/documents_db.svg'));
    iconRegistry.addSvgIcon('comments_db', sanitizer.bypassSecurityTrustResourceUrl('assets/images/comments_db.svg'));
    iconRegistry.addSvgIcon('return', sanitizer.bypassSecurityTrustResourceUrl('assets/images/returnWebArena.svg'));
    iconRegistry.addSvgIcon('logout', sanitizer.bypassSecurityTrustResourceUrl('assets/images/ausloggen.svg'));
    iconRegistry.addSvgIcon('user', sanitizer.bypassSecurityTrustResourceUrl('assets/images/user.svg'));

    iconRegistry.addSvgIcon('editor-menu', sanitizer.bypassSecurityTrustResourceUrl('assets/images/editor-menu.svg'));
    iconRegistry.addSvgIcon('user', sanitizer.bypassSecurityTrustResourceUrl('assets/images/user.svg'));
    iconRegistry.addSvgIcon('settings', sanitizer.bypassSecurityTrustResourceUrl('assets/images/einstellungen.svg'));
    iconRegistry.addSvgIcon(
      'select_profile',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/profil-wechseln.svg'),
    );
    iconRegistry.addSvgIcon('help', sanitizer.bypassSecurityTrustResourceUrl('assets/images/hilfe.svg'));
    iconRegistry.addSvgIcon('reset', sanitizer.bypassSecurityTrustResourceUrl('assets/images/zuruecksetzen.svg'));
    iconRegistry.addSvgIcon('arena', sanitizer.bypassSecurityTrustResourceUrl('assets/images/arena.svg'));
    iconRegistry.addSvgIcon('arena-Profil', sanitizer.bypassSecurityTrustResourceUrl('assets/images/arena-Profil.svg'));
    iconRegistry.addSvgIcon(
      'konfiguration-speichern',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/konfiguration-speichern.svg'),
    );
    iconRegistry.addSvgIcon('help', sanitizer.bypassSecurityTrustResourceUrl('assets/images/arena_help.svg'));

    iconRegistry.addSvgIcon(
      'arenaGeneral',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/Arena_General.svg'),
    );

    iconRegistry.addSvgIcon('arrow', sanitizer.bypassSecurityTrustResourceUrl('assets/images/KAOS_arena_arrow.svg'));
    iconRegistry.addSvgIcon('copy', sanitizer.bypassSecurityTrustResourceUrl('assets/images/copy.svg'));
    iconRegistry.addSvgIcon('layout', sanitizer.bypassSecurityTrustResourceUrl('assets/images/layout.svg'));
    iconRegistry.addSvgIcon(
      'user-setting',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/arena_user_settings.svg'),
    );
    iconRegistry.addSvgIcon(
      'arena-setting',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/arena_settings.svg'),
    );
    iconRegistry.addSvgIcon(
      'arena-refresh',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/arena_refresh.svg'),
    );

    // // addional settings tabs example
    // this.abcArenaService.additionalSettingsTabs = [
    //   {
    //     caption: 'login',
    //     name: 'Sub login',
    //     svgIcon: 'settings',
    //     visibleIndex: 10,
    //     component: LoginComponent,
    //     tabs: []
    //   },
    //   {
    //     caption: 'password settings',
    //     name: 'password settings',
    //     visibleIndex: 11,
    //     svgIcon: 'arena',
    //     component: UserSettingsComponent,
    //     tabs: [
    //     ]
    //   },
    // ];
  }

  private configureArenaService() {
    this.arenaService.onSettingsChanged$.subscribe(() => this.loadArenaUser());

    this.arenaService.updateHigonOnSave = async () => {
      if (this.higonComponent) {
        await this.higonComponent?.setCurrentObject(this.currentDataEntity);
      }
    };

    this.profileInfoObservable$ = this.arenaService.getCurrentProfile(true).pipe(
      map((profileInfo) => {
        if (profileInfo?.id) {
          this.currentProfileDisplayText = profileInfo.displayText;
          if (this.uiconfig.propertyDocumentSource) {
            this.pdcPropertiesService.getProperties(this.uiconfig.propertyDocumentSource).subscribe((p) => {
              this.pdcPropertiesService.propertiesVM = new PropertyViewModel(p);
            });
          }
        }
        return profileInfo;
      }),
      catchErrorAndRethrow((err) => this.abcCore.error.publish(err)),
    );
  }

  //#region ArenaActions
  private initializeArenaServiceActions() {
    this.origActions = this.abcArenaService.actions;
    this.abcArenaService.actions = {
      loadArenaNavigation: async (navDoc, loadOnly) => {
        return await this.loadArenaNavigation(navDoc, loadOnly);
      },
      updateCurrentNavigationNode: async (node) => {
        await this.updateCurrentNavigationNode(node);
      },
      loadArenaLayout: async (lvm, layoutPath, filter, objectIds, parameterValues, navstate) => {
        await this.loadArenaLayout(lvm, layoutPath, filter, objectIds, parameterValues, undefined, navstate);
      },
      loadExternalComponent: async (cmd) => {
        await this.loadExternalComponent(null, cmd);
      },
      getUIConfiguration: () => {
        return this.uiconfig;
      },
      refreshArenaObject: async (entity: ICoreDataEntity) => {
        return await this.refreshArenaObject(entity);
      },
      refreshArenaHignaObject: async (entity: ICoreDataEntity) => {
        return await this.refreshArenaHignaObject(entity);
      },
      getContext: () => this.context,
      getDataContext: () => this.currentDataContext,
      getLayoutContext: () => this.currentLayoutContext,
      getCurrentDataEntity: () => this.currentDataEntity,
      openSubLayout: async (target: TargetLayoutInfo) => {
        await this.openSubLayout(target);
      },
      showHideColumn: async (column: LayoutColumnViewModel) => {
        await this.hignaComponent?.showHideColumn(column);
      },
      findAdditionalHigonActions: async (objectType: string | number) => {
        return await this.findAdditionalHigonActions(objectType);
      },
      arenaLayout: {
        getCurrentOrFilteredDataEntities: async (allowMultiSelect?: boolean) => {
          return await this.getCurrentOrFilteredDataEntities(allowMultiSelect);
        },
        getFilteredDataEntities: async () => {
          return await this.getFilteredDataEntities();
        },
        removeGridObjectById: async (id: string) => {
          return await this.removeGridObjectById(id);
        },
        beginLoading: () => {
          return this.beginLoading();
        },
        endLoading: async (token: { active: boolean }, noChangeDetection?: boolean) => {
          return this.endLoading(token, noChangeDetection);
        },
        requery: async () => {
          return await this.requery();
        },
      },
    };
  }

  private beginLoading(): { active: boolean } {
    return this.dataFormComponent?.beginLoading();
  }

  private endLoading(token: { active: boolean }, noChangeDetection?: boolean): void {
    this.dataFormComponent?.endLoading(token, noChangeDetection);
  }

  private async loadArenaNavigation(navDoc: NavigationViewModel, loadOnly: boolean): Promise<void> {
    this.nvDocument_ = navDoc;
    if (navDoc?.groups) {
      this.nvGroup = navDoc.groups
        .filter((a) => a.data.VisibleIndex > -1)
        .map((grp) => {
          grp.data.IsVisible = true;
          return grp;
        });
      await this.pluginManager.update(navDoc, (ly) => this.getLayout(this.arenaService.arenaName, ly.layoutName));
      await this.pluginManager.updateNotificationInfos();
      this.handleOverflowingSideNavItems();
      if (
        !loadOnly &&
        !this.isStandaloneDataForm &&
        !this.currentNavigationGroup &&
        this.nvGroup.length &&
        !this.layoutPath &&
        this.uiconfig.renderDrawer &&
        this.uiconfig.renderNavigation &&
        this.context.openDefaultGroupMode !== ArenaDefaultGroupMode.None
      ) {
        if (this.context.openDefaultGroupMode === ArenaDefaultGroupMode.Layout) {
          await this.openArenaLayoutGroup();
        } else {
          await this.openNavigationGroup(this.nvGroup[0]);
        }
      }
    }
  }

  private handleOverflowingSideNavItems = (): void => {
    if (this.sideNavItemsContainer()) {
      this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        for (const entry of entries) {
          const root = document.documentElement;
          const sideNavItemHeight = Number(
            getComputedStyle(root).getPropertyValue('--side-bar-width').trim().split('px')[0],
          );
          const sideNavItemsContainerheight = entry.contentRect.height;

          const availableSlots = Math.floor(sideNavItemsContainerheight / sideNavItemHeight);
          // -1 for letting space for the dropdownbutton that contains the nested elements
          const nestedItemsCount = this.nvGroup.length - (availableSlots - 1);

          if (nestedItemsCount > 0) {
            this.nestedNvGroup = this.nvGroup.slice(this.nvGroup.length - nestedItemsCount);
          } else {
            this.nestedNvGroup = [];
          }

          this.nvGroup.map((grp) => {
            if (this.nestedNvGroup.includes(grp)) {
              grp.data.IsVisible = false;
            } else {
              grp.data.IsVisible = true;
            }
          });

          this.changeDetectorRef.detectChanges();
        }
      });

      this.resizeObserver.observe(this.sideNavItemsContainer().nativeElement);
    }
  };

  private async openArenaLayoutGroup(): Promise<void> {
    const group = this.nvGroup.find(
      (_) => _.data.VisibleIndex >= 0 && !_.data.Component && !_.data.Command && !_.data.CommandIdentifier,
    );
    if (group) {
      await this.openNavigationGroup(group, true);
    }
  }

  private async updateCurrentNavigationNode(node: NavigationNodeBaseViewModel) {
    if (!node) {
      this.abcSideNav?.setOpened(false);
    }
    if (this.currentNavigationNode === node) {
      return;
    }

    await this.updateCurrentNavigationNodeCore(node, true);
  }

  private async refreshArenaObject(entity: ICoreDataEntity) {
    if (!entity || this.currentDataEntity?.Id === entity.Id) {
      await this.notifyRowChanged(this.currentDataEntity);
      if (this.hignaComponent) {
        await this.hignaComponent.updateGridObjectById(entity.Id);
      }
    } else if (entity) {
      if (this.hignaComponent) {
        await this.hignaComponent.navigateToGridObjectById(entity.Id);
        await this.hignaComponent.updateGridObjectById(entity.Id);
      } else {
        await this.notifyRowChanged(entity);
      }
    }
  }

  private async refreshArenaHignaObject(entity: ICoreDataEntity) {
    if (!entity || this.currentDataEntity?.Id === entity.Id) {
      if (this.hignaComponent) {
        await this.notifyRowChanged(this.currentDataEntity);
        await this.hignaComponent.updateGridObjectById(entity.Id);
      }
    }
  }
  //#endregion

  private isOpeningNavigationGroup: boolean;
  private async updateCurrentNavigationNodeCore(
    node: NavigationNodeBaseViewModel,
    updateNavigation: boolean,
  ): Promise<void> {
    if (!this.isOpeningNavigationGroup) {
      this.isOpeningNavigationGroup = true;
      try {
        this.currentNode = node;
        if (updateNavigation) {
          await this.openNavigationGroup(node.mainGroup);
          this.navigationComponent?.expandToNode(node, true);
        }
        await this.updateUrl();

        if (node && !(node instanceof NavigationLayoutViewModel) && !node.mainGroup?.data?.Component) {
          this.abcSideNav?.setOpened(true);
        }
      } finally {
        this.isOpeningNavigationGroup = false;
      }
    }
  }

  private getCurrentDataObjectType(layoutViewModel?: LayoutViewModel): string | number {
    return this.context?.defaultObjectType ?? layoutViewModel?.objectType ?? this.layoutViewModel?.objectType;
  }

  private async loadArenaLayout(
    layoutViewModel: LayoutViewModel,
    layoutPath: string,
    filter: string,
    objectIds: string[],
    parameterValues: ParameterValue[],
    currentId?: string,
    navstate?: NavStateItem[],
  ): Promise<void> {
    if (
      this.layoutViewModel === layoutViewModel &&
      objectIds?.length === 1 &&
      !this.hignaComponent &&
      this.dataFormComponent &&
      this.getCurrentDataObjectType(layoutViewModel)
    ) {
      await this.notifyRowChanged({
        Id: objectIds[0],
        ObjectType: this.getCurrentDataObjectType(layoutViewModel),
      });
      return;
    }

    if (!layoutPath) {
      layoutPath = ArenaService.LayoutPathByLayoutIdPrefix + layoutViewModel.id;
    }

    const navstateInfo = await this.createTileNavState(layoutViewModel, layoutPath, filter, parameterValues, navstate);
    this.tileNavs = navstateInfo.tileNavs;
    filter = navstateInfo.filter;
    parameterValues = navstateInfo.parameterValues;

    if (this.abcArenaService.context.layoutUsage === LayoutUsage.New && this.currentDataEntity) {
      this.persistenceService.revertPendingChanges(this.arenaService.PERSISTENCE_CATEGORY);
      this.hignaComponent?.undoEditGridObject(null);
      this.abcArenaService.context.layoutUsage = LayoutUsage.Standard;
      this.arenaService.persistMode = PersistMode.Update;
    }

    setTimeout(() => {
      this.performLayoutAction(
        () =>
          this.loadArenaLayoutCore(
            layoutViewModel,
            layoutPath,
            filter,
            objectIds ?? this.context?.nextDataContext.objectids,
            parameterValues,
            currentId ?? this.context?.nextDataContext.currentid ?? this.lastObjectId,
            undefined,
            false,
          ),
        false,
      );
    }, 100);
  }

  private subscriptions: { dynamic: boolean; item: Subscription }[] = [];
  private currentProfileInfo: ProfileInfoViewModel;
  private async loadArenaLayoutCore(
    layoutViewModel: LayoutViewModel,
    layoutPath: string,
    filter: string,
    objectIds: string[],
    parameterValues: ParameterValue[],
    currentId: string,
    cachedGridObjects: unknown[],
    preserveGridFilter: boolean,
  ): Promise<void> {
    this.showLoader = true;
    this.lastObjectId = ''; //Must be '' because of later checks
    this.lastGridBottom = undefined;
    let isLoadingLayout = true;
    this.dataService.lastDataError = null;

    if (!this.currentProfileInfo) {
      const profileInfo = await this.profileService.getCurrentProfile(true).toPromise();
      this.isReadOnly = ConvertHelper.toBoolean(profileInfo?.settings?.Permissions?.isReadOnly);
      this.currentProfileInfo = new ProfileInfoViewModel(profileInfo);
    }
    if (this.isReadOnly) {
      layoutViewModel.isReadOnly = this.isReadOnly;
    }

    this.isBusy = () => isLoadingLayout;

    const drawerPanel: HTMLElement = document.querySelector('.dx-drawer-panel-content');
    if (drawerPanel) {
      drawerPanel.style.display = 'unset';
    }

    const tile = this.tileNavs[this.tileNavs.length - 1];
    const ctx: ArenaLayoutContext = tile.data;
    tile.disabled = true;

    let pluginLayoutPath = layoutPath;
    if (!pluginLayoutPath) {
      const navDoc = await this.getNavigation();
      pluginLayoutPath = navDoc?.findBestLayoutByLayoutId(layoutViewModel?.id)?.fullName;
    }
    this.currentLayoutPluginActions = await this.pluginManager.activateLayout(pluginLayoutPath);
    if (layoutViewModel) {
      layoutViewModel.currentCRUDPermissions = await this.currentLayoutPluginActions.findCRUDPermissions();
    }

    if (this.menuComponent) {
      let denyInsert: boolean = ctx?.target?.denyInsert || layoutViewModel?.denyInsert;

      if (!denyInsert) {
        const otvm = await this.abcCore.objectSpace
          .getObjectType(this.getCurrentDataObjectType(layoutViewModel))
          .toPromise();
        if (otvm) {
          for (const iterator of ObjectHelper.asArray(otvm?.requiredColumnsByName)) {
            const requiredColumn = iterator[1];
            if (
              (requiredColumn.reference ||
                (!otvm.baseType &&
                  requiredColumn.name === 'ParentId' &&
                  otvm?.requiredColumnsByName.has('ParentObjectType'))) &&
              requiredColumn.name !== ctx?.target?.column
            ) {
              const match = layoutViewModel.allColumns.find(
                (lcvm) => lcvm?.oPath?.replace('ref:', '') === requiredColumn?.name,
              );
              if (!match) {
                denyInsert = true;
                break;
              }
            }
          }
        }
      }

      this.onNotifyChanges = async (hasChanges) => {
        this.menuComponent?.notifyChanges(hasChanges);
        if (
          this.hignaComponent &&
          this.dataFormComponent?.settings?.selectedObject?.Object?.Id &&
          this.hignaComponent.abcGrid.focusedDataRowKey &&
          this.arenaService.persistMode !== PersistMode.Insert &&
          this.hignaComponent.abcGrid.focusedDataRowKey !== this.dataFormComponent.settings?.selectedObject?.Object?.Id
        ) {
          if (!hasChanges) {
            const e = await this.hignaComponent?.abcGrid?.getFocusedRowData();
            await this.notifyRowChanged(e);
          } else {
            await this.hignaComponent.setFocusedRow(this.dataFormComponent.settings?.selectedObject?.Object?.Id);
          }
        }
      };

      await this.menuComponent.loadLayout(
        layoutViewModel,
        !this.hignaComponent,
        denyInsert,
        async (menu) => await this.performMenuClick(menu),
        await this.currentLayoutPluginActions.findAdditionalMenuItems(),
      );
    }

    if (this.layoutViewModel && this.layoutViewModel !== layoutViewModel) {
      await this.notifyRowChanged(null);
    }

    this.createSubscriptions(true);

    if (this.hignaComponent) {
      let isNavigated = false;
      let focusedEntity: ICoreDataEntity;
      let nextEntity: ICoreDataEntity;
      let isChangingRow = false;
      this.subscriptions.push({
        dynamic: true,
        item: this.hignaComponent?.abcGrid.onFocusedRowChanged.subscribe(async (event: FocusedRowChangedEvent) => {
          let entity = (await this.hignaComponent?.abcGrid?.getFocusedRowData()) as ICoreDataEntity;
          if (entity) {
            if (!isNavigated) {
              focusedEntity = entity;
              await this.hignaComponent.ensureRowVisible(entity.Id);
              if (!isLoadingLayout) {
                if (this.lastObjectId != entity?.Id) {
                  await this.notifyRowChanged(entity);
                }
                entity = null;
                isNavigated = true;
              }
            }
          }
          nextEntity = entity;
          if (!isChangingRow && !isLoadingLayout) {
            isChangingRow = true;
            setTimeout(async () => {
              try {
                while (nextEntity) {
                  const e = nextEntity;
                  nextEntity = null;
                  focusedEntity = null;
                  await this.notifyRowChanged(e);
                }
              } finally {
                isChangingRow = false;
              }
            }, 10);
          }
        }),
      });

      this.subscriptions.push({
        dynamic: true,
        item: this.hignaComponent.abcGrid.onEditorPreparing.subscribe((event) => {
          if (event.parentType == 'filterRow' && event.editorOptions) {
            event.editorOptions.onFocusIn = async (args) => {
              await this.hignaComponent?.setFocusedRow(null);
              await this.notifyRowChanged(null);
            };
          }
        }),
      });

      let getCustomByIdFilters: () => Map<string, string[]>;
      if (
        this.currentNavigationNode instanceof NavigationLayoutViewModel &&
        this.currentNavigationNode.layoutName === layoutViewModel.id
      ) {
        const navigationLayout = this.currentNavigationNode;
        getCustomByIdFilters = () => navigationLayout.badgeInfos;
      }

      const dataSource = await this.hignaComponent.loadLayout(
        layoutViewModel,
        filter,
        objectIds,
        parameterValues,
        currentId,
        cachedGridObjects,
        preserveGridFilter,
        getCustomByIdFilters,
      );
      ctx.reload = async (currentId_: string, requery?: boolean, preserveGridFilter?: boolean) =>
        await this.loadArenaLayoutCore(
          layoutViewModel,
          layoutPath,
          filter,
          objectIds,
          parameterValues,
          currentId_,
          requery || !dataSource?.gridObjects ? undefined : await dataSource.gridObjects,
          preserveGridFilter,
        );
      ctx.reloadFiltered = async (objectIds_: string[]) =>
        await this.loadArenaLayoutCore(
          layoutViewModel,
          layoutPath,
          filter,
          objectIds_,
          parameterValues,
          this.lastObjectId,
          undefined,
          false,
        );

      let loopHandle = 0;
      loopHandle = window.setInterval(() => {
        if (!isNavigated) {
          let entity: ICoreDataEntity = focusedEntity;
          if (currentId && entity) {
            if (entity.Id == currentId) {
              this.hignaComponent?.setFocusedRow(currentId);
            } else {
              entity = null;
            }
          }
          if (!entity) {
            if (this.hignaComponent?.dataSource?.length) {
              entity = this.hignaComponent?.abcGrid.getRowData(0);
            }
            if (entity && !this.hignaComponent?.abcGrid.instance.isRowFocused(entity.Id)) {
              nextEntity = null;
              this.hignaComponent?.setFocusedRow(entity.Id);
            }
          }
          if (!isLoadingLayout) {
            if (this.lastObjectId != entity?.Id) {
              this.notifyRowChanged(entity);
            }
            clearInterval(loopHandle);
            loopHandle = 0;
          }
        } else {
          clearInterval(loopHandle);
          loopHandle = 0;
        }
      }, 1500);
      this.isBusy = () => isLoadingLayout || isChangingRow || loopHandle !== 0;

      this.subscriptions.push({
        dynamic: true,
        item: this.hignaComponent?.abcGrid.onFocusedRowChanging.subscribe((event: FocusedRowChangingEvent) => {
          if (!this.canNavigate()) {
            event.cancel = true;
            const grid = this.hignaComponent?.abcGrid?.instance;
            if (grid) {
              setTimeout(() => {
                grid.selectRows([], false);
              }, 500);
            }
          }
          //TODO: auto-save
          // let subscription: Subscription;
          // try {
          //   const subject = new Subject<void>();
          //   subscription = this.persistenceService.afterPersistPendingChanges$.subscribe((_) => {
          //     alert(JSON.stringify(_));
          //     if (_ instanceof ErrorInfo) {
          //       this.abcCore.error.publish(_);
          //       event.cancel = true;
          //     } else {
          //       event.cancel = false;
          //     }
          //     subject.next(null);
          //     subject.complete();
          //   });
          //   this.persistenceService.persistPendingChanges(this.arenaService.PERSISTENCE_CATEGORY);
          //   await subject.asObservable().toPromise();
          // } catch (err) {
          //   this.abcCore.error.publish(err);
          //   event.cancel = true;
          // }
          // subscription?.unsubscribe();
        }),
      });
    }

    let higonEntity: ICoreDataEntity;
    if (this.dataFormComponent) {
      this.dataFormComponent.suspendLoader = () => this.showLoader;

      await this.dataFormComponent.loadLayout(
        layoutViewModel,
        filter,
        objectIds || [currentId],
        parameterValues,
        this.onNotifyChanges,
      );

      if (!this.hignaComponent) {
        let entity: ICoreDataEntity;
        const objectType = this.getCurrentDataObjectType(layoutViewModel);
        if (objectIds?.length && objectType) {
          entity = { ObjectType: objectType, Id: objectIds[0] };
        } else if (this.abcArenaService.context.layoutUsage === LayoutUsage.New && objectType) {
          entity = { ObjectType: objectType, Id: null };
        }
        if (entity) {
          await this.notifyRowChanged(entity);
          higonEntity = entity;
        }
        this.showLoader = false;

        ctx.reload = async (currentId_: string, requery?: boolean, preserveGridFilter?: boolean) =>
          await this.loadArenaLayoutCore(
            layoutViewModel,
            layoutPath,
            filter,
            objectIds,
            parameterValues,
            currentId_,
            undefined,
            preserveGridFilter,
          );
      }
    }

    if (this.higonComponent) {
      this.higonComponent.getDataFormSettings = () => this.dataFormComponent?.settings;
      this.higonComponent.togglePDC = (identifier) => this.dataFormComponent?.togglePDC(identifier);
      this.higonComponent.openSubLayout = (target) => this.openSubLayout(target);

      await this.higonComponent.loadLayout(layoutViewModel, filter, objectIds, parameterValues);
      if (higonEntity) {
        await this.higonComponent.setCurrentObject(higonEntity);
      }
    }

    await this.updateUrl();
    isLoadingLayout = false;
    if (this.hignaComponent || this.dataFormComponent) {
      this.isNavigating = false;
    }

    this.notifyDataError();
  }

  private notifyDataError(): void {
    setTimeout(() => {
      if (this.dataService.lastDataError) {
        const err = this.dataService.lastDataError;
        this.dataService.lastDataError = null;
        if (!CacheStorageAdapter.isOffline) {
          this.arenaService.notificationSubject.next({
            type: ToastType.Error,
            message: this.abcCore.error.getFullErrorMessage(err),
          });
        }
        console.error(err);
      }
    }, 5000);
  }

  private get lastUrl(): string {
    return this.prepareLastUrl(this.abcArenaService.lastUrl);
  }
  private set lastUrl(value: string) {
    this.abcArenaService.lastUrl = value;
  }
  private get lastNavState(): string {
    return this.abcArenaService.lastNavState;
  }
  private set lastNavState(value: string) {
    this.abcArenaService.lastNavState = value;
  }
  private get lastHistoryUrl(): string {
    return this.prepareLastUrl(this.abcArenaService.lastHistoryUrl);
  }
  private set lastHistoryUrl(value: string) {
    this.abcArenaService.lastHistoryUrl = value;
  }
  private get nextUrlFragment(): string {
    return this.abcArenaService.nextUrlFragment;
  }
  private set nextUrlFragment(value: string) {
    this.abcArenaService.nextUrlFragment = value;
  }
  private get isNavigating(): boolean {
    return this.abcArenaService.isNavigating;
  }
  private set isNavigating(value: boolean) {
    this.abcArenaService.isNavigating = value;
  }
  private async updateUrl(): Promise<void> {
    if (
      this.isNavigating ||
      this.isStandaloneDataForm ||
      !(await this.userService.isCurrentlyLoggedInOrAnonymousAllowed.toPromise())
    ) {
      return;
    }

    const currentNode = this.currentNode ?? this.currentNavigationNode;
    const urlstring = this.buildUrlStringForLayout(this.layoutPath ?? currentNode?.fullName, false);

    let query: string = undefined;
    const fragment: string = this.nextUrlFragment;
    this.nextUrlFragment = undefined;
    const ctx: any = ObjectHelper.cloneDeep(this.currentDataContext);
    if (!this.uiconfig.url.higna && this.currentDataEntity?.Id) {
      ctx[ObjectHelper.nameof('currentid', this.currentDataContext)] = this.currentDataEntity?.Id;
    }
    ctx[ObjectHelper.nameof('objectids', this.currentDataContext)] = undefined;
    ctx[ObjectHelper.nameof('objecttype', this.currentDataContext)] = undefined;
    let navstate: string;
    if (this.needsNavState()) {
      navstate = await this.serializeNavState(this.getCurrentNavState(null, null, null, null, null, null));
    }
    ctx[ObjectHelper.nameof('navstate', this.currentDataContext)] = navstate;

    ObjectHelper.replaceValuesDeep(ctx, null, undefined);
    const p = this.getQueryParams(this.uiconfig.url, ctx);
    if (!CompareHelper.isEmptyObject(p)) {
      const urltree = new UrlTree();
      urltree.root = new UrlSegmentGroup([], {});
      urltree.queryParams = p;
      const serializer = new DefaultUrlSerializer();
      query = serializer.serialize(urltree)?.split('?')[1];
    }

    let fullurlstring = urlstring;
    if (query) {
      fullurlstring += '?' + query;
    }
    if (fragment) {
      fullurlstring += '#' + fragment;
    }
    const lastHistoryUrl = this.lastHistoryUrl;
    const lastUrl = this.lastUrl;
    if (fullurlstring !== lastHistoryUrl) {
      if (
        this.layoutPath &&
        ((this.needsNavState() && !navstate) || (lastUrl === urlstring && this.lastNavState === navstate))
      ) {
        this.updateWindowUrl(fullurlstring, false);
      } else {
        this.updateWindowUrl(fullurlstring, true);
        this.lastUrl = urlstring;
        this.lastNavState = navstate;
        this.lastHistoryUrl = fullurlstring;
      }
    }
  }

  private prepareLastUrl(url: string): string {
    if (url?.startsWith('/')) {
      return url.substring(1);
    } else {
      return url;
    }
  }

  private updateWindowUrl(fullurlstring: string, includeInHistory?: boolean) {
    if (includeInHistory) {
      history.pushState(undefined, undefined, fullurlstring);
    } else {
      history.replaceState(undefined, undefined, fullurlstring);
      //this.location.replaceState(urlstring, query);
    }
  }

  public updateWindowQueryParams(query: URLSearchParams, includeInHistory?: boolean) {
    let url = window.location.href.split('?')[0];
    const queryString = query.toString();
    if (queryString) {
      url += '?' + queryString;
    }
    if (this.nextUrlFragment) {
      url += '#' + this.nextUrlFragment;
      this.nextUrlFragment = undefined;
    }
    if (url !== window.location.href) {
      this.updateWindowUrl(url, includeInHistory);
    }
  }

  private buildUrlStringForLayout(layoutPath: string, forNewWindow: boolean) {
    const parts: string[] = [];
    const url: string[] = [];
    if (this.isStandaloneDataForm && forNewWindow && this.arenaService.arenaName === ProfileInfo.defaultArenaName) {
      parts.push(StringHelper.trimEnd(window.location.origin, '/'));
      parts.push(...StringHelper.trim(window.location.pathname, '/').split('/'));
      parts[parts.length - 1] = 'arena';
      url.push(this.arenaService.arenaName);
    } else {
      if (this.abcConfigService.configuration.AllowCustomRoute) {
        url.push(this.arenaService.arenaName);
      }
    }

    if (layoutPath) {
      url.push(...layoutPath?.split('\\'));
    }
    parts.push(...url.map((_) => this.prepareUrlSegment(_, true)));
    return parts.join('/');
  }

  private prepareUrlSegment(url: string, encode: boolean): string {
    if (url) {
      if (encode) {
        return encodeURIComponent(url);
      } else {
        return decodeURIComponent(url);
      }
    }
    return url;
  }

  private getQueryParams(ui: ArenaUIParams, data: {}): Params {
    return ObjectHelper.mergeDeep({}, ui.getChanges(), data);
  }

  private isOpeningSubLayout = false;
  private async openSubLayout(target: TargetLayoutInfo): Promise<void> {
    if (this.isOpeningSubLayout || !target?.layout || !this.canNavigate()) {
      return;
    }
    try {
      if (!target.openInNewTab) {
        this.isOpeningSubLayout = true;
        for (const tile of this.tileNavs) {
          tile.disabled = false;
        }
      }
      const layout: LayoutViewModel = await this.getLayoutFromTarget(target);
      let nextLayoutPath: string;
      let openInNewTab: boolean;
      let navLayout: NavigationLayoutViewModel;
      if (target?.layout instanceof NavigationLayoutViewModel) {
        nextLayoutPath = target.layout.fullName;
        if (target.layout.nodeFilter) {
          nextLayoutPath = ArenaService.LayoutPathByLayoutIdPrefix + target.layout.layoutName;
        }
        if (target.openInNewTab) {
          openInNewTab = true;
        }
        navLayout = target.layout;
      } else if (target?.layout instanceof LayoutViewModel) {
        const ly = (await this.getNavigation()).findBestLayoutByLayoutId(target?.layout?.id);
        if (ly && !ly?.nodeFilter) {
          nextLayoutPath = ly.fullName;
        } else {
          nextLayoutPath = ArenaService.LayoutPathByLayoutIdPrefix + target.layout.id;
        }
        if (target.openInNewTab) {
          openInNewTab = true;
        }
        navLayout = ly;
      }

      if (openInNewTab) {
        const url =
          this.buildUrlStringForLayout(nextLayoutPath, true) +
          '?' +
          ObjectHelper.nameof('navstate', this.currentDataContext) +
          '=' +
          (await this.serializeNavState(
            this.getCurrentNavState(
              target.targetLayoutOPath,
              target.column,
              target.isColumnInTarget,
              target.denyInsert,
              target.sourceId,
              target.sourceObjectType,
            ),
          ));
        window.open(url, '_blank');
      } else {
        const s = this.hignaComponent?.abcGrid?.storageSettings;
        if (s) {
          s.includeFiltering = true;
        }
        const tile: AbcTileNav = new AbcTileNav(
          layout?.caption,
          layout?.id,
          false,
          this.createLayoutContext(
            layout,
            nextLayoutPath,
            target?.targetLayoutOPath,
            this.currentLayoutContext?.parameterValues,
            target,
          ),
        );
        tile.disabled = true;
        this.tileNavs.push(tile);
        if (!this.hignaComponent) {
          this.changeDetectorRef.detectChanges();
        }
        if (navLayout && this.tileNavs.length === 1) {
          await this.updateCurrentNavigationNodeCore(navLayout, true);
        }

        await this.openSubLayoutCore(target, null, layout);
      }
    } catch (err) {
      this.abcCore.error.publish(err);
      this.isOpeningSubLayout = false;
    }
  }

  private async createTileNavState(
    layoutViewModel: LayoutViewModel,
    layoutPath: string,
    filter: string,
    parameterValues: ParameterValue[],
    navstate: NavStateItem[],
  ): Promise<{
    tileNavs: AbcTileNav[];
    filter: string;
    parameterValues: ParameterValue[];
  }> {
    let tileNavs: AbcTileNav[] = [];
    const newfilter = filter;
    let newparameterValues: ParameterValue[] = parameterValues;
    let lastTarget: TargetLayoutInfo;
    if (navstate?.length) {
      let lastId: string;
      for (const n of navstate) {
        if (n.layoutPath) {
          const nav = (await this.getNavigation()).findLayoutByFullName(n.layoutPath, false);
          if (nav) {
            const ly = await this.getLayout(this.arenaService.arenaName, nav.layoutName);
            if (ly) {
              const ctx = this.createLayoutContext(ly, n.layoutPath, n.layoutFilter, n.parameterValues, lastTarget);
              let currentParameterValues = n.parameterValues;
              if (lastId) {
                currentParameterValues = ParameterHelper.combineParameterValues(currentParameterValues, [
                  new ParameterValue(ParameterNames.IdOfObjectInParentLayout, lastId),
                ]);
              }
              ctx.reload = async (currentId_: string, requery?: boolean, preserveGridFilter?: boolean) =>
                await this.loadArenaLayoutCore(
                  ly,
                  n.layoutPath,
                  n.layoutFilter,
                  null,
                  currentParameterValues,
                  currentId_,
                  undefined,
                  preserveGridFilter,
                );
              ctx.reloadFiltered = async (objectIds_: string[]) =>
                await this.loadArenaLayoutCore(
                  ly,
                  n.layoutPath,
                  n.layoutFilter,
                  objectIds_,
                  currentParameterValues,
                  n.currentId,
                  undefined,
                  false,
                );
              tileNavs.push(new AbcTileNav(ly?.caption ?? ly.id, ly?.id, false, ctx));
              if (tileNavs.length === navstate.length) {
                if (n.nextFilter) {
                  filter = this.dataService.linkOPathExpressions(filter, [n.nextFilter], 'AND');
                }
                newparameterValues = ParameterHelper.combineParameterValues(parameterValues, [
                  new ParameterValue(ParameterNames.IdOfObjectInParentLayout, n.currentId),
                ]);
              }
              lastTarget = {
                layout: ly,
                targetLayoutOPath: n.nextFilter,
                sourceId: n.currentId,
                sourceObjectType: n.currentObjectType,
                column: n.nextFilter ? n.nextColumn : n.column,
                isColumnInTarget: n.nextFilter ? n.isNextColumnInTarget : n.isColumnInTarget,
                denyInsert: n.nextFilter ? n.nextDenyInsert : n.denyInsert,
              };
              lastId = n.currentId;
            } else {
              tileNavs = [];
              break;
            }
          }
        } else {
          filter = this.dataService.linkOPathExpressions(filter, [n.nextFilter], 'AND');
          newparameterValues = ParameterHelper.combineParameterValues(parameterValues, n.parameterValues);
        }
      }
    }

    const lastTileNav = new AbcTileNav(
      layoutViewModel?.caption ?? layoutViewModel?.id,
      layoutViewModel?.id,
      true,
      this.createLayoutContext(
        layoutViewModel,
        layoutPath,
        navstate?.length === 1 && !navstate[0].layoutPath ? newfilter : filter,
        navstate?.length === 1 && !navstate[0].layoutPath ? newparameterValues : parameterValues,
        lastTarget,
      ),
    );
    tileNavs.push(lastTileNav);

    return {
      tileNavs: tileNavs,
      filter: filter,
      parameterValues: newparameterValues,
    };
  }

  private getCurrentNavState(
    nextFilter: string,
    nextColumn: string,
    isNextColumnInTarget: boolean,
    nextDenyInsert: boolean,
    currentId: string,
    currentObjectType: string | number,
  ): NavStateItem[] {
    return ArenaComponent.getNavStateCore(
      this.tileNavs,
      nextFilter,
      nextColumn,
      isNextColumnInTarget,
      nextDenyInsert,
      currentId,
      currentObjectType,
    );
  }

  private static getNavStateCore(
    tileNavs: AbcTileNav[],
    nextFilter: string,
    nextColumn: string,
    isNextColumnInTarget: boolean,
    nextDenyInsert: boolean,
    currentId: string,
    currentObjectType: string | number,
  ): NavStateItem[] {
    const navstate: NavStateItem[] = [];
    if (tileNavs.length > 0) {
      for (let i = 0; i < tileNavs.length; i++) {
        const tile = tileNavs[i];
        const ctx: ArenaLayoutContext = tile.data;
        if (nextFilter || i < tileNavs.length - 1 || (i === 0 && this.needsNavStateCore(i, ctx))) {
          let id: string;
          let ot: string | number;
          let f: string;
          let c: string;
          let ct: boolean;
          let denyInsert: boolean;
          let layoutPath: string;
          layoutPath = ctx.layoutPath;
          if (tileNavs.length === 1 && !nextFilter && i === 0 && this.needsNavStateCore(i, ctx)) {
            layoutPath = undefined;
          }
          if (!nextFilter || i < tileNavs.length - 1) {
            if (i < tileNavs.length - 1) {
              const nextctx: ArenaLayoutContext = tileNavs[i + 1].data;
              id = nextctx?.target?.sourceId;
              ot = nextctx?.target?.sourceObjectType;
              f = nextctx?.target?.targetLayoutOPath;
              c = nextctx?.target?.column;
              ct = nextctx?.target?.isColumnInTarget;
              denyInsert = nextctx?.target?.denyInsert;
            }
          } else {
            f = nextFilter;
            c = nextColumn;
            ct = isNextColumnInTarget;
            denyInsert = nextDenyInsert;
          }
          if (i == tileNavs.length - 1 && currentId) {
            id = currentId;
            ot = currentObjectType;
          }
          navstate.push({
            layoutPath: layoutPath,
            layoutFilter: ctx.layoutFilter,
            parameterValues: ctx.parameterValues,
            currentId: id,
            currentObjectType: ot,
            column: ctx.target?.column,
            isColumnInTarget: ctx.target?.isColumnInTarget,
            denyInsert: ctx.target?.denyInsert,
            nextFilter: f,
            nextColumn: c,
            nextDenyInsert: denyInsert,
            isNextColumnInTarget: ct,
          });
        }
      }
    } else if (nextFilter) {
      navstate.push({
        layoutPath: undefined,
        layoutFilter: undefined,
        parameterValues: undefined,
        currentId: currentId,
        currentObjectType: currentObjectType,
        column: undefined,
        isColumnInTarget: undefined,
        denyInsert: undefined,
        nextFilter: nextFilter,
        nextColumn: nextColumn,
        isNextColumnInTarget: isNextColumnInTarget,
        nextDenyInsert: nextDenyInsert,
      });
    }

    return navstate;
  }

  private static navStateNames = {
    l: ObjectHelper.nameof<NavStateItem>('layoutPath'),
    f: ObjectHelper.nameof<NavStateItem>('layoutFilter'),
    p: ObjectHelper.nameof<NavStateItem>('parameterValues'),
    i: ObjectHelper.nameof<NavStateItem>('currentId'),
    o: ObjectHelper.nameof<NavStateItem>('currentObjectType'),
    c: ObjectHelper.nameof<NavStateItem>('column'),
    t: ObjectHelper.nameof<NavStateItem>('isColumnInTarget'),
    d: ObjectHelper.nameof<NavStateItem>('denyInsert'),
    nf: ObjectHelper.nameof<NavStateItem>('nextFilter'),
    nc: ObjectHelper.nameof<NavStateItem>('nextColumn'),
    nt: ObjectHelper.nameof<NavStateItem>('isNextColumnInTarget'),
    nd: ObjectHelper.nameof<NavStateItem>('nextDenyInsert'),
  };
  private async deserializeNavState(navstate: string): Promise<NavStateItem[]> {
    if (navstate) {
      const arr = await this.compression.ungzipJsonFromBase64<any>(navstate);
      const result: NavStateItem[] = [];
      for (const o of arr) {
        const n = new NavStateItem();
        for (const key in ArenaComponent.navStateNames) {
          const name = ArenaComponent.navStateNames[key];
          n[name] = o[key];
        }
        result.push(n);
      }
      return result;
    }
  }

  private async serializeNavState(navstate: NavStateItem[]): Promise<string> {
    if (navstate) {
      const arr: any[] = [];
      for (const n of navstate) {
        const o = {};
        for (const key in ArenaComponent.navStateNames) {
          const name = ArenaComponent.navStateNames[key];
          const v = n[name];
          const isArray = ObjectHelper.isArray(v);
          if ((isArray && v.length) || (!isArray && !CompareHelper.isNullorFalse(v))) {
            o[key] = v;
          }
        }
        arr.push(o);
      }
      return await this.compression.gzipJsonAsBase64(arr);
    }
  }

  private needsNavState(): boolean {
    return ArenaComponent.needsNavStateCore(this.tileNavs?.length - 1, this.currentLayoutContext);
  }

  private static needsNavStateCore(index: number, ctx: ArenaLayoutContext): boolean {
    return index > 0 || (index == 0 && ctx?.parameterValues?.length > 0);
  }

  private createLayoutContext(
    layout: LayoutViewModel,
    layoutPath: string,
    layoutFilter: string,
    parameterValues: ParameterValue[],
    target?: TargetLayoutInfo,
  ): ArenaLayoutContext {
    const ctx = new ArenaLayoutContext();
    ctx.layout = layout;
    ctx.layoutPath = layoutPath;
    ctx.layoutFilter = layoutFilter;
    ctx.parameterValues = parameterValues;
    ctx.target = target;
    return ctx;
  }

  private async getLayoutFromTarget(target: TargetLayoutInfo): Promise<LayoutViewModel> {
    if (target?.layout instanceof LayoutViewModel) {
      return target.layout;
    } else {
      return await this.getLayout(this.arenaService.arenaName, target?.layout?.layoutName);
    }
  }

  private async openSubLayoutCore(target: TargetLayoutInfo, lastId: string, layout?: LayoutViewModel): Promise<void> {
    if (!layout) {
      layout = await this.getLayoutFromTarget(target);
    }

    await this.loadArenaLayoutCore(
      layout,
      target.layout instanceof NavigationLayoutViewModel ? target.layout.fullName : null,
      target.targetLayoutOPath,
      null,
      [new ParameterValue(ParameterNames.IdOfObjectInParentLayout, target.sourceId)],
      lastId,
      undefined,
      false,
    );
  }

  private async notifyRowChanged(entity: ICoreDataEntity): Promise<void> {
    this.dataService.lastDataError = null;
    this.showLoader = false;
    this.currentDataEntity = entity;
    this.currentDataContext.currentid = entity?.Id;
    if (entity?.Id) {
      this.lastObjectId = entity.Id;
    }
    this.isOpeningSubLayout = false;
    await this.updateUrl();

    try {
      await this.hignaComponent?.notifyCurrentObject(entity);
    } catch (err) {
      this.abcCore.error.publish(err);
    }

    try {
      await this.dataFormComponent?.setCurrentObject(entity, this.onNotifyChanges);
    } catch (err) {
      this.abcCore.error.publish(err);
    }
    try {
      await this.higonComponent?.setCurrentObject(entity);
    } catch (err) {
      this.abcCore.error.publish(err);
    }
    try {
      await this.menuComponent?.setCurrentObject(entity);
    } catch (err) {
      this.abcCore.error.publish(err);
    }
    try {
      await this.currentLayoutPluginActions?.notifyCurrentObject(entity);
    } catch (err) {
      this.abcCore.error.publish(err);
    }

    this.context.nextDataContext = new ArenaDataContext();

    if (this.dataFormComponent?.settings?.gridOnFormInstances?.length) {
      this.updateHignaHeight(undefined);
    }

    this.notifyDataError();
  }

  private async getIfSettingsAllowed(): Promise<boolean> {
    let settingsAllowed = true;
    if (DotNetBridge.hasCommandHandler) {
      settingsAllowed = await this.arenaService.isDotNetStandalone();
    }
    return await settingsAllowed;
  }

  private loadArenaUser(): void {
    this.arenaUserObservable$ = this.arenaService.getLoginInfo().pipe(
      concatMap((loginInfo) => {
        return this.userPersonInfoService.getCurrentUserPersonInfo().pipe(
          map((userPersonInfo) => {
            const arenaUser: ArenaUser = new ArenaUser();
            arenaUser.displayText = loginInfo?.displayName || loginInfo?.userName;
            arenaUser.imgPortrait = userPersonInfo?.ImgPortrait;
            return arenaUser;
          }),
        );
      }),
    );
  }

  private layoutAction: () => Promise<void>;

  async ngOnInit(): Promise<void> {
    this.userService.canLogout.subscribe((_) => {
      this.canLogout = _;
      this.changeDetectorRef.detectChanges();
    });
    this.createSubscriptions(false);

    // this.onResize(document.defaultView.innerHeight);
    this.arenaComponentLoadedSubject.next(true);

    this.screenHeight = window.innerHeight;
  }

  ngAfterViewChecked(): void {
    if (this.navigationComponent) {
      this.navigationComponent.loadExternalComponent = async (command) =>
        await this.loadExternalComponent(null, command);
      this.navigationComponent.loadLayout = async (layout) => {
        await this.arenaService.loadGroupOrLayout(this.arenaService.arenaName, layout);
      };
      this.navigationComponent.onCurrentNodeChanging = (node) => {
        return this.canNavigate();
      };
      this.navigationComponent.onCurrentNodeChanged = (node) => {
        this.uiconfig.url.higna = true;
        this.updateCurrentNavigationNodeCore(node, false);
      };
    }

    if (this.layoutAction) {
      setTimeout(() => {
        this.performLayoutAction(this.layoutAction, true);
      }, 100);
    }
  }

  async getArenaProfiles(): Promise<string[]> {
    if (!this.arenaProfiles) {
      this.arenaProfiles = ConvertHelper.arrayBufferToObjectArray<string>(
        await lastValueFrom(
          this.abcCore.data.executeGetPublicNamesQuery(
            new ParameterValue('ArenaSettings.' + this.arenaService.arenaName),
          ),
        ),
      ).sort((a, b) => a.localeCompare(b));
    }
    return this.arenaProfiles;
  }

  private async performLayoutAction(action: () => Promise<void>, delayRetry: boolean): Promise<void> {
    if (action) {
      if (this.hignaComponent || this.dataFormComponent) {
        this.layoutAction = null;
        try {
          await action();
          let caption = this.layoutViewModel?.caption;
          if (!caption && this.tileNavs.length) {
            caption = this.tileNavs[0]?.caption;
          }
          this.abcArenaService.arenaStatus({
            Status: Status.Success,
            LayoutCaption: caption,
          });
        } catch (err) {
          console.log(err);
          this.arenaService.notificationSubject.next({
            type: ToastType.Error,
            message:
              (this.layoutViewModel?.id ? this.res('Arena-WrongLayout') + `: ${this.layoutViewModel?.id},` : '') +
              this.res('Arena-LayoutPath') +
              `: ${this.layoutPath}`,
          });
          this.abcArenaService.arenaStatus({
            Status: Status.Error,
            Message: err,
          });
        }
      } else {
        this.layoutAction = action;
        if (delayRetry) {
          setTimeout(async () => {
            this.changeDetectorRef.detectChanges();
          }, 100);
        } else {
          this.changeDetectorRef.detectChanges();
        }
      }
    }
  }

  private async applyPersistedStyleSettings(): Promise<void> {
    try {
      let styleSettingsJson = '';
      const cachedStyleSettings = localStorage.getItem('ArenaStyleSettings');
      if (cachedStyleSettings) {
        styleSettingsJson = cachedStyleSettings;
      }

      if (styleSettingsJson) {
        const trimmedSettings = styleSettingsJson.trim();

        // Skip if settings are empty, default, or not valid JSON
        if (
          !trimmedSettings ||
          trimmedSettings === 'default' ||
          (trimmedSettings[0] !== '{' && trimmedSettings[0] !== '[')
        ) {
          return;
        }

        // Parse the JSON style configuration
        const styleConfig = JSON.parse(styleSettingsJson) as {
          Mode?: string;
          LabelMode?: string;
          Colors?: { [k: string]: string };
        };

        // Apply settings to the document root element
        const rootElement = document.documentElement;

        // Apply label mode if specified
        if (styleConfig?.LabelMode) {
          rootElement.style.setProperty('--label-mode', styleConfig.LabelMode);
        }

        // Apply custom CSS color variables
        if (styleConfig?.Colors) {
          const colorKeys = Object.keys(styleConfig.Colors);
          for (const colorKey of colorKeys) {
            const colorValue = styleConfig.Colors[colorKey];
            rootElement.style.setProperty(`--${colorKey}`, colorValue);
          }
        }
      }
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  private async applyDataFormLayoutSettingsFromSettings(): Promise<void> {
    try {
      const currentConfiguration = await lastValueFrom(this.dataFormSettingsService.getCurrentSettings());
      const styleConfig = currentConfiguration?.style;

      if (styleConfig?.tabs) {
        this.uiconfig.dataFormStyle = styleConfig?.tabs;
      }
      if (styleConfig?.orientation) {
        this.uiconfig.layoutSplitterOrientation = styleConfig?.orientation;
      }

      if (styleConfig?.stylingMode) {
        this.uiconfig.stylingMode = styleConfig?.stylingMode;
        this.abcCoreComponentService.stylingMode = styleConfig?.stylingMode;
      }

      if (currentConfiguration && currentConfiguration?.theme) {
        this.themeService.applyTheme(currentConfiguration?.theme);
      }
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  private isBusy: () => boolean = () => false;
  private async performMenuClick(menu: MenuButton): Promise<boolean> {
    if (this.isBusy && this.isBusy()) {
      console.warn('Arena is busy, click ignored.');
      return false;
    }

    const persistenceCategory: string = this.arenaService.PERSISTENCE_CATEGORY;
    if (menu === MenuButton.Save) {
      let _persistMode: PersistMode;
      try {
        if (CompareHelper.isNullorEmpty(this.arenaService.persistMode)) {
          if (this.abcArenaService.context.layoutUsage === LayoutUsage.New) {
            this.arenaService.persistMode = PersistMode.Insert;
          } else {
            this.arenaService.persistMode = PersistMode.Update;
          }
        }
        _persistMode = this.arenaService.persistMode;
        const pendingCommentEditors: PendingCommentEditor[] = this.collectDirtyCommentEditors();

        if (_persistMode === PersistMode.Insert) {
          this.arenaService.insertedDataEntity = async (entity: ICoreDataEntity) => {
            this.currentDataEntity = {
              Id: entity.Id,
              ObjectType: entity.ObjectType,
            };

            let gridObject = entity;
            if (this.hignaComponent) {
              gridObject = await this.hignaComponent.insertGridObjectById(entity.Id);
            }
            this.abcArenaService.context.layoutUsage = LayoutUsage.Standard;
            await this.notifyRowChanged(gridObject);
            this.persistCommentEditors(pendingCommentEditors);
            this.arenaService.persistMode = undefined;
          };
        } else {
          this.arenaService.updatedDataEntity = async (entity: ICoreDataEntity) => {
            let gridObject = entity;
            if (this.hignaComponent) {
              gridObject = await this.hignaComponent.updateGridObjectById(this.currentDataEntity.Id);
            }
            await this.notifyRowChanged(gridObject);
            this.arenaService.persistMode = undefined;
          };
          // Parent already exists, so its comments can be persisted up front.
          this.persistCommentEditors(pendingCommentEditors);
        }

        this.dataFormComponent?.settings?.setValidationActive(false);
        this.persistenceService.persistPendingChanges(persistenceCategory, null, (err) => {
          this.dataFormComponent?.settings?.setValidationActive(true);
          this.onNotifyChanges(true);
        });

        return true;
      } catch (error) {
        this.dataFormComponent?.settings?.setValidationActive(true);
        this.arenaService.persistMode = _persistMode;
        this.abcCore.error.publish(error);
        return false;
      }
    } else if (menu === MenuButton.Add) {
      try {
        this.arenaService.persistMode = PersistMode.Insert;
        this.abcArenaService.context.layoutUsage = LayoutUsage.New;
        this.persistenceService.clearPendingObjects(persistenceCategory);
        this.dataFormComponent?.settings?.setValidationActive(false);

        if (this.hignaComponent) {
          await this.hignaComponent.addEmptyRowToGrid();
        }

        await this.notifyRowChanged({
          ObjectType: this.currentDataEntity?.ObjectType ?? this.getCurrentDataObjectType(),
          Id: null,
        });

        return true;
      } catch (error) {
        this.dataFormComponent?.settings?.setValidationActive(true);
        this.abcCore.error.publish(error);
        return false;
      }
    } else if (menu === MenuButton.Undo) {
      try {
        this.persistenceService.revertPendingChanges(persistenceCategory);
        this.abcArenaService.context.layoutUsage = LayoutUsage.Standard;

        if (this.arenaService.persistMode === PersistMode.Insert) {
          if (this.lastObjectId) {
            await this.notifyRowChanged(this.currentDataEntity);
          } else {
            await this.notifyRowChanged(null);
          }
          if (this.hignaComponent) {
            await this.hignaComponent.undoEditGridObject(this.lastObjectId);
          }
        } else {
          await this.notifyRowChanged(this.currentDataEntity);
        }
        this.dataFormComponent?.settings?.setValidationActive(false);

        return true;
      } catch (error) {
        this.dataFormComponent?.settings?.setValidationActive(true);
        this.abcCore.error.publish(error);
        return false;
      }
    } else if (menu === MenuButton.Delete) {
      this.arenaService.persistMode = PersistMode.Delete;
      this.showMessageBoxAndPerformAction(async () => {
        this.arenaService.deletedDataEntity = async (e) => {
          this.abcArenaService.context.layoutUsage = LayoutUsage.Standard;
          const id = e.Id;
          this.currentDataEntity = null;
          await this.removeGridObjectById(id);
          this.arenaService.persistMode = undefined;
          this.dataFormComponent?.settings?.setValidationActive(false);
        };
        await this.arenaService.deleteDataEntity(this.currentDataEntity);
      }, MessageMode.Question);
    } else if (menu === MenuButton.Report) {
      try {
        const entities = await this.getCurrentOrFilteredDataEntities();
        if (entities) {
          await this.openBceAndExecuteDocument(entities, false);
          return true;
        }
        return false;
      } catch (error) {
        this.abcCore.error.publish(error);
        return false;
      }
    } else if (menu === MenuButton.DmlQuery) {
      try {
        await this.openBceAndExecuteDocument(await this.getCurrentOrFilteredDataEntities(), true);
        return true;
      } catch (error) {
        this.abcCore.error.publish(error);
        return false;
      }
    }
  }

  private collectDirtyCommentEditors(): PendingCommentEditor[] {
    const result: PendingCommentEditor[] = [];
    const settings = this.dataFormComponent?.settings;
    if (!settings) {
      return result;
    }
    for (const group of settings.layoutViewModel.groups) {
      for (const column of group.columns) {
        if (column.specialType === SpecialType.Comment && column.isEditableComment) {
          const editorKey: string = column.layoutColumnObject.Key;
          const htmlEditor: AbcHtmlEditorComponent = settings.abcComponentInstances[editorKey];
          if (htmlEditor?.isDirty) {
            result.push({ key: editorKey, value: htmlEditor.value });
          }
        }
      }
    }
    return result;
  }

  private persistCommentEditors(commentEditors: PendingCommentEditor[]): void {
    const settings = this.dataFormComponent?.settings;
    if (!settings) {
      return;
    }
    for (const commentEditor of commentEditors) {
      const htmlEditor: AbcHtmlEditorComponent = settings.abcComponentInstances[commentEditor.key];
      if (htmlEditor) {
        htmlEditor.value = commentEditor.value;
        htmlEditor.onSave.emit(commentEditor.value);
      }
    }
  }

  private async removeGridObjectById(id: string): Promise<number> {
    if (this.hignaComponent) {
      const rowCount = await this.hignaComponent.removeGridObjectById(id);
      if (!rowCount) {
        await this.notifyRowChanged(null);
      }
      return rowCount;
    } else {
      await this.notifyRowChanged(null);
    }
  }

  private async getFilteredDataEntities(): Promise<ICoreDataEntity[]> {
    if (this.hignaComponent) {
      return await this.hignaComponent.getFilteredDataEntities();
    } else if (this.currentDataEntity) {
      return [this.currentDataEntity];
    } else {
      return [];
    }
  }

  private async getCurrentOrFilteredDataEntities(allowMultiSelect?: boolean): Promise<ICoreDataEntity[]> {
    const filtered = await this.getFilteredDataEntities();
    if (allowMultiSelect === false) {
      if (this.currentDataEntity?.Id) {
        return [this.currentDataEntity];
      } else if (filtered.length > 0) {
        return [filtered[0]];
      } else {
        return [];
      }
    } else if (filtered.length > 1 && this.currentDataEntity) {
      const messageSettings = new MessageSettings();
      messageSettings.button = MessageBoxButtons.YesNoCancel;
      messageSettings.caption = 'Aktuelles Objekt?';
      messageSettings.icon = MessageBoxIcon.Question;
      const div = document.createElement('div');
      div.innerHTML = this.arenaService.res('CurrentObjects-Question', filtered.length.toString(), '<br/>');

      messageSettings.htmlcode = div;
      const dialogResult = await this.messageBoxService.showMessageBox(messageSettings).toPromise();
      if (dialogResult['result'] === DialogResult.Cancel) {
        return undefined;
      } else if (dialogResult['result'] === DialogResult.No) {
        return [this.currentDataEntity];
      } else {
        return filtered;
      }
    } else {
      return filtered;
    }
  }

  public async navigateToTileNav(event: OnTileClickEvent): Promise<void> {
    const tile = event.data;
    if (this.canNavigate()) {
      const nextIndex = this.tileNavs.indexOf(tile) + 1;
      const lastTarget: TargetLayoutInfo = this.tileNavs[nextIndex]?.data?.target;
      const lastId: string = lastTarget?.sourceId;
      this.tileNavs.splice(nextIndex);
      tile.disabled = true;
      if (!this.uiconfig.renderHigna) {
        this.changeDetectorRef.detectChanges();
      }
      this.lastHistoryUrl = undefined;
      this.lastUrl = undefined;
      this.lastNavState = undefined;
      const ctx: ArenaLayoutContext = tile.data;
      const target: TargetLayoutInfo = ctx?.target;
      if (ctx?.reload) {
        await ctx?.reload(lastId, false, true);
      } else if (target) {
        await this.openSubLayoutCore(target, lastId);
      }
    }
  }

  private unsubscribe(dynamicOnly: boolean): void {
    const list = this.subscriptions;
    this.subscriptions = [];
    for (const s of list) {
      try {
        if (!dynamicOnly || s?.dynamic) {
          s?.item?.unsubscribe();
        } else {
          this.subscriptions.push(s);
        }
      } catch (err) {
        console.error(err);
      }
    }
  }

  private createSubscriptions(dynamicOnly: boolean): void {
    this.unsubscribe(dynamicOnly);

    if (!dynamicOnly) {
      this.subscriptions.push({
        dynamic: false,
        item: this.route?.data?.subscribe(async (data) => {
          if (this.isStandaloneDataForm) {
            return;
          }
          this.updateApplicationTitle(data.applicationTitle);
          window.parent.document.title = this.uiconfig.applicationTitle;

          if (data.applicationIcon) {
            this.uiconfig.applicationIcon = data.applicationIcon;
            this.iconRegistry.addSvgIcon(
              this.uiconfig.applicationIcon,
              this.sanitizer.bypassSecurityTrustResourceUrl(`assets/images/${this.uiconfig.applicationIcon}.svg`),
            );
          }
        }),
      });

      this.subscriptions.push({
        dynamic: false,
        item: this.route?.url?.subscribe(
          async (url) => {
            try {
              const oldparts = this.lastHistoryUrl?.split('#');
              const urlstring: string = this.prepareLastUrl(this.router.url);
              const newparts = urlstring?.split('#');
              if (newparts?.length > 1) {
                this.nextUrlFragment = newparts[1];
              }
              if (oldparts?.length > 0 && newparts?.length > 1 && oldparts[0] == newparts[0]) {
                let el = document.getElementById(this.nextUrlFragment);
                if (!el) {
                  const nodes = document.getElementsByName(this.nextUrlFragment);
                  if (nodes?.length > 0) {
                    el = nodes.item(0);
                  }
                }
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth' });
                }
              } else {
                this.isNavigating = true;
                await this.onUrlChanged(url, this.route.snapshot.queryParamMap);
                if (!this.lastHistoryUrl) {
                  this.lastHistoryUrl = urlstring;
                }
              }
            } catch (err) {
              console.error(err);
            }
          },
          (err) => console.error(err),
        ),
      });

      this.subscriptions.push({
        dynamic: false,
        item: this.router.events.subscribe(
          async (event: NavigationStart) => {
            if (event.navigationTrigger === 'popstate') {
              try {
                const url = this.router.parseUrl(event.url);
                const newUrl = this.prepareLastUrl(event.url.split('?')[0]);
                const queryParamsChanged =
                  this.lastUrl === newUrl && this.lastHistoryUrl !== this.prepareLastUrl(event.url);
                this.lastHistoryUrl = event.url;
                this.isNavigating = true;
                this.lastUrl = newUrl;
                this.lastNavState = url.queryParamMap.get('navstate');
                //await this.onUrlChanged(url.root.children['primary'].segments, url.queryParamMap);
                await this.router.navigateByUrl(url, { replaceUrl: true });
                if (queryParamsChanged) {
                  this.queryParamsStateChanged.emit(url.queryParamMap);
                }
              } catch (err) {
                console.error(err);
              }
            }
          },
          (err) => console.error(err),
        ),
      });

      this.subscriptions.push({
        dynamic: false,
        item: this.arenaService.notification$?.subscribe({
          next: (notification) => {
            if (this.displayNotification(notification)) {
              const notifAttr: INotification = {
                id: 'error-notification',
                message: notification.message,
                type: notification.type,
                displayTime: notification.type === 'error' ? 8000 : 4000,
              };
              return this.abcContainer.createComponent(this.abcNotification, AbcNotificationComponent, notifAttr)
                .componentRef.instance;
            }
          },
        }),
      });

      this.arenaService.loadContextMenu = true;
    }

    if (this.hignaComponent) {
      this.subscriptions.push({
        dynamic: true,
        item: this.hignaComponent?.onObjectsSelected?.subscribe((dataEntities) => {
          this.onObjectsSelected.emit(dataEntities);
        }),
      });
    }
  }

  private async onUrlChanged(url: UrlSegment[], params: ParamMap): Promise<void> {
    if (this.isStandaloneDataForm) {
      return;
    }
    let layoutPath: string;
    if (url?.length && url[0]?.path?.toLowerCase() !== 'login' && url[0]?.path?.toLowerCase() !== 'undefined') {
      let arr = url;
      if (this.abcConfigService.configuration.AllowCustomRoute) {
        this.uiconfig.arenaName = url[0].path;
        if (this.uiconfig.arenaName?.toLowerCase() !== ProfileInfo.defaultArenaName?.toLowerCase()) {
          this.updateApplicationTitle(this.uiconfig.arenaName);
        }
        arr = url.slice(1);
      }
      layoutPath = arr.map((_) => this.prepareUrlSegment(_.path, false)).join('\\');
    }

    try {
      if (params) {
        const paramsObj = this.uiconfig.url;
        const arr: { value: string; key: string }[] = [];
        for (const key of params.keys) {
          const value = params.get(key);
          arr.push({ value: value, key: key });
        }
        for (const e of arr) {
          const key = e.key;
          const value = e.value;
          const objkey = key.toLowerCase();
          if (objkey === ObjectHelper.nameof('currentid', this.context.nextDataContext)) {
            this.context.nextDataContext.currentid = value;
          } else if (objkey === ObjectHelper.nameof('objectids', this.context.nextDataContext) && value) {
            this.context.nextDataContext.objectids = value.split(',');
          } else if (objkey === 'objectid' && value) {
            this.context.nextDataContext.objectids = [value];
          } else if (objkey === ObjectHelper.nameof('objecttype', this.context.nextDataContext) && value) {
            this.context.nextDataContext.objecttype = value;
          } else if (objkey === ObjectHelper.nameof('navstate', this.context.nextDataContext) && value) {
            this.context.nextDataContext.navstate = value;
          } else {
            const objvalue = paramsObj[objkey];
            if (value && typeof objvalue === 'boolean') {
              paramsObj[objkey] = ConvertHelper.toBoolean(value);
            }
          }
        }
        if (!this.uiconfig.url.higna) {
          if (!this.context.nextDataContext.objectids?.length && this.context.nextDataContext.currentid) {
            this.context.nextDataContext.objectids = [this.context.nextDataContext.currentid];
          }
          if (layoutPath && !this.context.nextDataContext.objectids?.length && !this.currentDataEntity?.Id) {
            this.uiconfig.url.higna = true;
          }
        }
      }
    } catch {}

    let navstate: NavStateItem[];
    if (this.context.nextDataContext?.navstate) {
      for (let i = this.tileNavs.length - 2; i >= 0; i--) {
        const currentNavState = ArenaComponent.getNavStateCore(
          this.tileNavs.slice(0, i + 1),
          null,
          null,
          null,
          null,
          null,
          null,
        );
        if ((await this.serializeNavState(currentNavState)) === this.context.nextDataContext?.navstate) {
          await this.navigateToTileNav({ index: i, data: this.tileNavs[i] });
          return;
        }
      }
      navstate = await this.deserializeNavState(this.context.nextDataContext?.navstate);
    }
    if (layoutPath || this.context.nextDataContext.objecttype) {
      if (
        this.currentLayoutContext?.reloadFiltered &&
        this.context.nextDataContext?.objectids?.length &&
        ((!this.context.nextDataContext.objecttype && this.currentLayoutContext?.layoutPath === layoutPath) ||
          (this.context.nextDataContext.objecttype &&
            (
              await this.abcCore.objectSpace.getObjectType(this.context.nextDataContext.objecttype).toPromise()
            ).isMatchOrBase(this.currentLayoutContext?.layout?.objectType)))
      ) {
        await this.currentLayoutContext.reloadFiltered(this.context.nextDataContext?.objectids);
        return;
      }
      if (layoutPath?.startsWith(ArenaService.LayoutPathByLayoutIdPrefix)) {
        this.context.openDefaultGroupMode = ArenaDefaultGroupMode.Layout;
      } else {
        this.context.openDefaultGroupMode = ArenaDefaultGroupMode.None;
      }
      await this.arenaService.loadGroupOrLayout(this.uiconfig.arenaName, layoutPath, undefined, navstate);
    } else {
      this.context.openDefaultGroupMode = ArenaDefaultGroupMode.Any;
      await this.arenaService.loadWebArena(this.uiconfig.arenaName);
      this.isNavigating = false;
    }
  }

  private updateApplicationTitle(applicationTitle: any) {
    if (this.abcConfigService.configuration.AllowCustomRoute) {
      this.uiconfig.applicationTitle =
        this.uiconfig.arenaName ||
        applicationTitle ||
        this.arenaService.arenaName ||
        window.parent.document.title ||
        'Web arena';
    } else {
      this.uiconfig.applicationTitle =
        this.abcConfigService.configuration?.ApplicationTitle ||
        applicationTitle ||
        window.parent.document.title ||
        'Web arena';
    }
  }

  private displayNotification(notification: Notification): boolean {
    let retDisable = this.abcArenaService.disableNotification;
    if (!retDisable) {
      if (this.abcArenaService.disableNotificationOnSuccess) {
        if (notification.type === ToastType.Error) {
          retDisable = false;
        } else {
          retDisable = true;
        }
      }
    }

    return !retDisable;
  }

  private async showMessageBoxAndPerformAction(action: () => Promise<void>, messageMode: MessageMode) {
    const settings = new MessageSettings();
    settings.mode = MessageMode[MessageMode[messageMode]];
    settings.icon = MessageBoxIcon[MessageMode[messageMode]];
    settings.button = MessageBoxButtons.YesNo;
    settings.message = this.arenaService.res('Menu-ConfirmDeleteMessage');
    const result = await this.messageBoxService.showMessageBox(settings).toPromise();

    if (DialogResult.Yes === result['result']) {
      await action();
    }
  }

  private async getFilterObjectTypesAndIds(
    entities: ICoreDataEntity[],
  ): Promise<{ objectTypeNames: string[]; ids: string[] }> {
    const objectTypeNames: string[] = [];
    const ids: string[] = [];
    if (entities) {
      const objectTypes: Map<string | number, ObjectTypeInfoViewModel> = new Map();
      for (const e of entities) {
        if (!objectTypes.get(e.ObjectType)) {
          const ot = await this.abcCore.objectSpace.getObjectType(e.ObjectType).toPromise();
          if (ot) {
            objectTypes.set(e.ObjectType, ot);
          }
        }
      }
      for (const ot of objectTypes.values()) {
        let current = ot;
        while (current && !objectTypeNames.find((_) => _ === current.name)) {
          objectTypeNames.push(current.name);
          current = current.baseType;
        }
      }
    }

    return { objectTypeNames: objectTypeNames, ids: ids };
  }

  private async openBceAndExecuteDocument(entities: ICoreDataEntity[], dmlQueries: boolean): Promise<void> {
    const filterInfo = await this.getFilterObjectTypesAndIds(entities);
    if (!filterInfo?.objectTypeNames?.length) {
      return;
    }

    const xpathFilters = this.getXPathFiltersByObjectTypes(filterInfo.objectTypeNames, entities.length > 1);

    const groupIds: GroupPacket[] = [];

    let xpathFilter: string;
    if (dmlQueries) {
      if (!this.abcArenaService.actions?.getUIConfiguration()?.isQueryingDisabled) {
        groupIds.push(GroupPacket.GROUP_ID_OPATHQUERIES);
      }
      xpathFilter = xpathFilters.dmlquery;
    } else {
      if (!this.abcArenaService.actions?.getUIConfiguration()?.isReportingDisabled) {
        groupIds.push(GroupPacket.GROUP_ID_OPATHREPORTS);
      }
      if (!this.abcArenaService.actions?.getUIConfiguration()?.isQueryingDisabled) {
        groupIds.push(GroupPacket.GROUP_ID_OPATHQUERIES);
      }
      xpathFilter = xpathFilters.query;
    }
    await this.bceService
      .selectDocument(groupIds, undefined, xpathFilter)
      .pipe(
        map(async (bceDoc) => {
          try {
            const isQueryDocument = bceDoc.documentType === BCEDocumentType.OPathQueryDocument;
            const isDmlQueryDocument = isQueryDocument && (await this.bceService.isDmlQueryDocument(bceDoc.Id));
            await this.executeBceDocument(
              bceDoc.Id,
              bceDoc.Designation,
              isDmlQueryDocument
                ? ArenaBceDocumentType.DmlQuery
                : isQueryDocument
                  ? ArenaBceDocumentType.Query
                  : ArenaBceDocumentType.Report,
              entities,
            );
          } catch (error) {
            this.abcCore.error.publish(error);
          }
        }),
      )
      .toPromise();
  }

  private async executeBceDocument(
    documentId: string,
    name: string,
    type: ArenaBceDocumentType,
    entities: ICoreDataEntity[],
  ): Promise<void> {
    const oldcursor = document.documentElement.style.cursor;
    try {
      document.documentElement.style.cursor = 'wait';
      const readDocumentQuery: ReadDocumentModel = new ReadDocumentModel();
      readDocumentQuery.Id = documentId;
      const query: QueryObjectsModel = ConvertHelper.arrayBufferToObject(
        await this.abcCore.data.executeReadQueryFromDocumentQuery(readDocumentQuery).toPromise(),
      );

      await QueryHelper.applyObjectFilterToQuery(this.abcCore.objectSpace, query, entities);

      document.documentElement.style.cursor = oldcursor;

      const parameters = this.parameterService.tryFindChangeableParameters(query);
      const parameterValues: ParameterValue[] = await this.parameterService.fillParametersIfExist(parameters);
      if (parameters?.length && !parameterValues?.length) {
        //Cancelled
        return;
      }
      document.documentElement.style.cursor = 'wait';

      if (type === ArenaBceDocumentType.DmlQuery) {
        QueryHelper.ensureQueryParameterValues(query, parameterValues);
        await this.bceService.executeDmlQuery(query, name);
        await this.requery();
      } else if (type === ArenaBceDocumentType.Query) {
        QueryHelper.ensureQueryParameterValues(query, parameterValues);
        await this.bceService.executeOPathQuery(query, name);
      } else {
        const fileReader = new FileReader();

        // this will change to a model
        const exportDocumentResultQuery: {
          Format: string;
          Id: string;
          Parameters: ParameterValue[];
        } = {
          Format: 'Pdf',
          Id: documentId,
          Parameters: [],
        };
        QueryHelper.ensureQueryParameterValues(exportDocumentResultQuery, query.Parameters);
        QueryHelper.ensureQueryParameterValues(exportDocumentResultQuery, parameterValues);

        const reportInfo: Blob = await this.abcCore.data.exportDocumentResult(exportDocumentResultQuery).toPromise();

        await fileReader.readAsDataURL(reportInfo);
        const fileBlob = new Blob([reportInfo], {
          type: 'application/pdf',
          endings: 'transparent',
        });
        const downloadUrl = URL.createObjectURL(fileBlob);
        const newWindow = this.abcCore.openWindow(downloadUrl);

        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          const downloadLink = document.getElementById('dataFormComponent')?.appendChild(document.createElement('a'));
          downloadLink.setAttribute('download', name);
          downloadLink.setAttribute('href', downloadUrl);
          downloadLink.click();
        }
      }
    } catch (err) {
      this.abcCore.error.publish(err);
    } finally {
      document.documentElement.style.cursor = oldcursor;
    }
  }

  private async requery(): Promise<void> {
    const reload = this.currentLayoutContext?.reload;
    if (reload) {
      await reload(this.currentDataEntity?.Id, true, true);
    }
  }

  private getXPathFiltersByObjectTypes(objectTypeNames: string[], multiSelectOnly: boolean) {
    const xPathObjectTypeFilter: string = objectTypeNames.map((_) => `@objectType='${_}'`).join(' or ');
    const xPathParameterObjectTypeFilters: string[] = objectTypeNames.map(
      (_) =>
        `@templateName='DefaultDataAccess' and DataExpression[@templateParameter='${_}' or starts-with(@templateParameter, '${
          _ + '#'
        }')]`,
    );
    const xPathParameterObjectTypeFilterAll: string = xPathParameterObjectTypeFilters.join(' or ');
    const xPathParameterObjectTypeFilter: string = xPathParameterObjectTypeFilters
      .map((_) => _ + (multiSelectOnly ? ` and @allowMultiSelect='true'` : ``))
      .join(' or ');
    const xPathQueryFilter = `(//ObjectQueryDocument)[1][not(@dmlType) and ObjectQueries/ObjectQuery[${xPathObjectTypeFilter}] and not(//ParameterConfiguration/ParameterDefinitions/ParameterDefinition[${xPathParameterObjectTypeFilterAll}])]`;
    const xPathParameterFilter = `//ObjectQueryDocument[not(@dmlType)]/../ParameterConfiguration/ParameterDefinitions/ParameterDefinition[${xPathParameterObjectTypeFilter}]`;
    const xPathDmlQueryFilter = `(//ObjectQueryDocument)[1][@dmlType!='' and ObjectQueries/ObjectQuery[${xPathObjectTypeFilter}] and not(//ParameterConfiguration/ParameterDefinitions/ParameterDefinition[${xPathParameterObjectTypeFilterAll}])]`;
    const xPathDmlParameterFilter = `//ObjectQueryDocument[@dmlType!='']/../ParameterConfiguration/ParameterDefinitions/ParameterDefinition[${xPathParameterObjectTypeFilter}]`;

    return {
      query: [xPathQueryFilter, xPathParameterFilter].join(' or '),
      dmlquery: [xPathDmlQueryFilter, xPathDmlParameterFilter].join(' or '),
    };
  }
  protected onResize(event: ResizeEvent): void {
    const matDrawerWidth = <HTMLElement>event.element?.firstChild;
    this.dataFormComponent?.setDrawerOpened(false, true, matDrawerWidth.clientWidth);
    this.sideNavWidth = matDrawerWidth.clientWidth;
    this.sideNavIsResized = true;
  }

  public toggleHigonDrawer() {
    if (this.dataFormComponent) {
      this.abcDrawer.opened = !this.abcDrawer.opened;
      const drawerClick = this.abcDrawer.opened;
      this.dataFormComponent.setDrawerOpened(drawerClick);
    }
    if (this.abcDrawer.opened) {
      document.getElementById('returnIcon')?.classList.add(this.rtlEnabled ? 't3ArenaRtl' : 't3');
      document.getElementById('returnIcon')?.classList.remove(this.rtlEnabled ? 't1ArenaRtl' : 't1');
    } else {
      document.getElementById('returnIcon')?.classList.remove(this.rtlEnabled ? 't3ArenaRtl' : 't3');
      document.getElementById('returnIcon')?.classList.add(this.rtlEnabled ? 't1ArenaRtl' : 't1');
    }
  }

  public openSettings() {
    //ComponentNameRegistry.get('SettingsComponent') not working in dotnet env.
    const cpt: ComponentRegistryEntry = ComponentRegister.get('SettingsComponent');

    const component: SettingsComponent = cpt?.component ?? SettingsComponent;
    let style: WindowStyle = cpt?.windowStyle;

    if (!style) {
      const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      const isXs = vw <= 576;
      const isSm = vw > 576 && vw <= 992;
      style = {
        height: isXs ? '85vh' : '600px',
        width: isXs ? '96vw' : isSm ? '70vw' : '40vw',
        minHeight: isXs ? '400px' : '600px',
        minWidth: isXs ? '280px' : '400px',
        maxHeight: '99%',
        maxWidth: '99%',
        resizable: !isXs,
        shading: true,
      };
    }
    const WindowAttr: IWindow = {
      id: 'window',
      label: this.arenaService.res('Arena-Settings'),
      maximizeButton: true,
      minimizeButton: true,
      component: component,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };
    this.abcWindowCpt = this.abcContainer.createComponent(this.abcWindow, AbcWindowComponent, WindowAttr);
    this.windowInstance = <AbcWindowComponent>this.abcWindowCpt.componentRef.instance;
    this.windowInstance.createComponentInstance().subscribe((activeComponent) => {
      activeComponent.openWindow();
      activeComponent.onContentReady.subscribe((event) => {
        const contentElement = event.component.content();
        contentElement.style.overflow = 'auto';
      });
      const sub = this.windowInstance.onAfterClosed.subscribe((e) => {
        this.windowInstance.ngOnDestroy();
        sub.unsubscribe();
      });
    });
  }

  private refreshDataFormDesign(selectedIndex: number): void {
    const sideNavClick = this.abcSideNav?.opened;
    if (this.arenaService.selectedNavMenuIndex === -1 && selectedIndex === 1) {
      this.dataFormComponent?.transformOfGallery(sideNavClick, this.sideNavWidth);
    }

    if (this.arenaService.selectedNavMenuIndex !== selectedIndex) {
      if (!sideNavClick) {
        this.dataFormComponent?.transformOfGallery(sideNavClick, this.sideNavWidth);
      }
    }

    if (this.arenaService.selectedNavMenuIndex >= 0 && this.arenaService.selectedNavMenuIndex === selectedIndex) {
      this.dataFormComponent?.setDrawerOpened(sideNavClick, this.sideNavIsResized, this.sideNavWidth);
    } else {
      this.arenaService.selectedNavMenuIndex = selectedIndex;
    }
  }

  private destroyExternalComponent(): void {
    const externalComponentDiv = document.getElementById('externalComponent');
    externalComponentDiv?.remove();
    if (this.customComponent) {
      this.customComponent.componentRef?.destroy();
    }
  }

  private async loadExternalComponent(navigationGroup: NavigationGroupViewModel, command?: Command): Promise<void> {
    if (this.abcSideNav) {
      await this.abcSideNav.setOpened(false);
    }
    this.tileNavs = [];
    await this.updateUrl();
    this.destroyExternalComponent();

    if (this.higonComponent) {
      const drawerClick = this.abcDrawer.opened;
      this.dataFormComponent?.setDrawerOpened(drawerClick, this.sideNavIsResized, this.sideNavWidth);
      document.getElementById('returnIcon')?.classList.remove(this.rtlEnabled ? 't3ArenaRtl' : 't3');
      document.getElementById('returnIcon')?.classList.add(this.rtlEnabled ? 't1ArenaRtl' : 't1');
      this.abcDrawer.opened = false;
    }
    const drawerPanel: HTMLElement = document.querySelector('.dx-drawer-panel-content');
    if (drawerPanel) {
      drawerPanel.style.display = 'none';
    }
    this.arenaService.externalComponent = true;
    const externalComponentDiv = document.getElementById('externalComponent');
    const cre: ComponentRegistryEntry = ComponentRegister.get(navigationGroup?.data?.Component ?? command.Identifier);
    const div = externalComponentDiv ? externalComponentDiv : document.createElement('div');
    div.setAttribute('id', 'externalComponent');
    div.setAttribute('fxFlex', '100');
    div.style.height = '100%';
    div.style.overflow = 'auto';
    this.clearDrawer(div);
    this.customComponent = this.arenaService.loadComponent(cre.component, div);

    if (
      ObjectHelper.implementsInterface(this.customComponent.componentRef?.instance, Interfaces.ISupportsWindowAdapter)
    ) {
      this.customComponent.componentRef.instance.setWindowAdapter(this);
    }

    if (navigationGroup?.markerTag) {
      const routerPath: string = this.router.config?.find(
        (config) => config.data?.MarkerTag === navigationGroup.name,
      )?.path;

      const externalRoute: Route = Routing.findRoute(this.router.config, routerPath, navigationGroup.markerTag);
      const navigationInfo: NavigationInfo = Routing.createNavigationInfo(externalRoute, this.route);
      this.router.navigate(navigationInfo.Commands, navigationInfo.Extras);
    }
  }

  private clearDrawer(div) {
    const drawerContainer = document.getElementById('drawerContainer');
    if (drawerContainer) {
      drawerContainer.appendChild(div);
      drawerContainer.style.boxShadow = 'none';
      drawerContainer.style.padding = '0px';
    }
  }

  private setDrawer() {
    const drawerContainer = document.getElementById('drawerContainer');
    if (drawerContainer) {
      drawerContainer.style.padding = '24px';
    }
  }

  ngOnDestroy() {
    this.abcArenaService.actions = this.origActions;
    this.isInitialized = false;

    if (this.isStandaloneDataForm) {
      return;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.arenaService.destroyObservables.next(null);
    this.destroyExternalComponent();
    this.unsubscribe(false);
  }

  public logout() {
    if (this.beforeLogoutFunc) {
      this.beforeLogoutFunc();
    }

    this.userService
      .logoutCurrentUser(true)
      .pipe(takeUntil(this.destroyApp$))
      .subscribe({
        error: (err) => this.abcCore.error.publish(err),
      });
    this.windowInstance?.closeDialog();
  }

  public async openArenaProfilePopup(isSaveMode: boolean): Promise<void> {
    await this.getArenaProfiles();
    const settings = await lastValueFrom(this.appSettingsService.getCurrentSettings());
    this.selectedArenaProfile = settings?.lastPublicName ?? '';
    this.arenaProfilesPopup.instance.show();
    this.isSavingArenaProfile = isSaveMode;
  }

  public closeArenaProfilesPopup(): void {
    this.arenaProfilesPopup.instance.hide();
  }

  public async syncArenaProfile(): Promise<void> {
    await lastValueFrom(
      this.appSettingsService.setCurrentSettings({
        lastPublicName: this.selectedArenaProfile,
      }),
    );
    if (this.isSavingArenaProfile) {
      const settings = await lastValueFrom(this.arenaSettingsService.getCurrentSettings());
      try {
        await lastValueFrom(
          this.abcCore.data.executeSetValueQuery(
            new ParameterValue(
              'ArenaSettings.' + this.arenaService.arenaName,
              JSON.stringify(settings),
              this.selectedArenaProfile,
            ),
          ),
        );
        this.arenaProfiles.includes(this.selectedArenaProfile) || this.arenaProfiles.push(this.selectedArenaProfile);
        this.arenaProfiles.sort((a, b) => a.localeCompare(b));
      } catch (error) {
        this.abcCore.error.publish(error);
      }
    } else {
      await this.loadAndApplyArenaSettings(this.selectedArenaProfile);
      window.location.reload();
    }
    this.closeArenaProfilesPopup();
  }

  public async resetDefaultSettings(): Promise<void> {
    await lastValueFrom(this.appSettingsService.setCurrentSettings({ lastPublicName: '' }));
    await this.loadAndApplyArenaSettings();
  }

  private async loadAndApplyArenaSettings(publicName?: string): Promise<void> {
    const result = ConvertHelper.arrayBufferToObject<IArenaSettings>(
      await lastValueFrom(
        this.abcCore.data.executeGetValueQuery(
          new ParameterValue('ArenaSettings.' + this.arenaService.arenaName, null, publicName),
        ),
      ),
    );
    await lastValueFrom(this.arenaSettingsService.setCurrentSettings(result));
    window.location.reload();
  }

  public dividerPanMove(e) {
    this.updateHignaHeight(e?.height);
    this.hignaComponent.abcGrid.instance.updateDimensions();
    /*
    if (childDxGallery?.length == 0) {
      this.tabs.style.setProperty('padding', 0 + 'px', 'important');
      dataFormContainer.style.setProperty('padding-bottom', 0 + 'px', 'important');
      fixGroupContainer.style.setProperty('height', '100' + '%', 'important');
      fixGroupContainer.style.setProperty('overflow', 'auto');
    }*/
  }

  private lastGridBottom: number;
  private updateHignaHeight(splitterHeight: number): void {
    const hignaContainer = document.querySelector('#hignaComponent') as HTMLElement;
    const drawerContainer = document.getElementById('drawerContainer');
    const galleryContainer = document.getElementById('gallery');
    const layoutSplitterComponent = document.querySelector('#layoutSplitterComponent') as HTMLElement;
    const splitterRect = layoutSplitterComponent?.getBoundingClientRect();
    if (!splitterRect) {
      return;
    }
    let gridBottom = splitterRect.bottom;
    let fullUpdate = true;
    if (splitterHeight === undefined) {
      splitterHeight = splitterRect.height;
      const galleryRect = galleryContainer?.getBoundingClientRect();
      if (galleryRect) {
        gridBottom = galleryRect.bottom;
      }
      fullUpdate = false;
    }
    if (gridBottom === this.lastGridBottom) {
      return;
    }
    this.lastGridBottom = gridBottom;
    const dividerContainer: HTMLElement = document.querySelector('.horizontalBehaviour');

    const drawerContainerHeight = drawerContainer.clientHeight;
    let hignaHeight = drawerContainerHeight - splitterHeight;
    const hignaHeightMin = 180;
    if (hignaHeight < hignaHeightMin) {
      hignaHeight = hignaHeightMin;
    }

    const styledGrids: Map<HTMLElement, number> = new Map();
    const styleFunc: (el: HTMLElement) => void = (el) => {
      el.querySelectorAll('.abcGridOnForm')?.forEach((gof) => {
        const gofElement = gof as HTMLElement;
        if (styledGrids.get(gofElement) !== this.lastGridBottom) {
          const gridRect = gofElement.getBoundingClientRect();
          const maxHeight = this.lastGridBottom - gridRect.top - 12;
          gofElement.style.setProperty('max-height', maxHeight + 'px', 'important');
          styledGrids.set(gofElement, this.lastGridBottom);
        }
      });
    };
    styleFunc(drawerContainer);

    if (
      galleryContainer &&
      this.dataFormComponent?.settings &&
      !this.dataFormComponent.settings.galleryMutationObserver
    ) {
      this.dataFormComponent.settings.galleryMutationObserver = new MutationObserver((arr) => {
        for (const r of arr) {
          if (r.target instanceof HTMLElement) {
            let el = r.target;
            while (el) {
              if (el === galleryContainer) {
                styleFunc(el);
                break;
              } else {
                el = el.parentElement;
              }
            }
          }
        }
      });
      this.dataFormComponent.settings.galleryMutationObserver.observe(galleryContainer, {
        subtree: true,
        childList: true,
      });
    }

    if (fullUpdate) {
      hignaContainer?.style.setProperty('height', hignaHeight + 'px', 'important');
      hignaContainer?.style.setProperty('max-height', hignaHeight - 48 + 'px', 'important');
      hignaContainer?.style.setProperty('min-height', '100px', 'important');
      layoutSplitterComponent?.style.setProperty(
        'max-height',
        drawerContainerHeight - hignaHeightMin + 'px',
        'important',
      );
    }
    dividerContainer?.style.setProperty('flex', 'none', 'important');
    dividerContainer?.style.setProperty('max-height', 'none', 'important');

    this.hignaComponent?.abcGrid?.instance?.updateDimensions();
  }

  // @HostListener('window:resize', ['$event.target.innerHeight'])
  // public onResize(val) {
  //   this.frame();
  // }

  public selectProfile(forwardTo?: string) {
    return this.profileService.navigateToProfileSelectionPage(null, null, 'arena');
  }

  public get areProfilesSupported(): boolean {
    return this.arenaService.areProfilesSupported;
  }

  public openHelp() {
    this.abcCore.openWindow(this.uiconfig.helpUrl);
  }

  private setSideNavProperties(opened: boolean, defaultWidth: number = 250): void {
    if (opened) {
      this.sideNavWidth = defaultWidth;
      this.sideNavIsResized = false;
    } else {
      this.sideNavIsResized = true;
    }
  }

  public async openNavigationGroupByName(groupName: string, activateOnly?: boolean): Promise<boolean> {
    const grp = this.nvGroup.find((_) => _?.name === groupName);
    if (grp) {
      return await this.openNavigationGroup(grp, activateOnly);
    }
  }

  public async openNestedNavigationGroup(event: ItemClickEvent): Promise<void> {
    const group = event.itemData;
    if (group) {
      await this.openNavigationGroup(group, undefined, true);
    }
  }

  public async openNavigationGroup(
    group: NavigationGroupViewModel,
    activateOnly?: boolean,
    toggle?: boolean,
  ): Promise<boolean> {
    if (!group || this.isStandaloneDataForm || !this.canNavigate()) {
      return;
    }

    this.arenaService.callNavigationNodeClick();

    if (this.currentNavigationGroup === group) {
      if (!group?.data?.Component) {
        if (this.navigationComponent?.currentGroup !== group) {
          this.navigationComponent?.loadArenaNavigationGroup(group);
        }
        if (toggle) {
          this.setSideNavProperties(!this.abcSideNav.opened);
          await this.abcSideNav?.setOpened(!this.abcSideNav.opened);
          this.dataFormComponent?.setDrawerOpened(!this.abcSideNav.opened, this.sideNavIsResized, this.sideNavWidth);
        } else if (
          !this.isNavigating &&
          this.abcSideNav &&
          !this.currentDataContext?.objectids?.length &&
          !this.context.nextDataContext?.objectids?.length &&
          this.uiconfig.renderNavigation &&
          activateOnly === undefined
        ) {
          await this.abcSideNav?.setOpened(true);
          this.dataFormComponent?.setDrawerOpened(!this.abcSideNav.opened);
        }
      }
      return false;
    } else {
      this.currentNavigationGroup = group;
      this.currentNode = null;
      this.navigationComponent?.loadArenaNavigationGroup(group);
      await this.updateCurrentNavigationNodeCore(group, false);

      if (group?.data?.Component) {
        await this.loadExternalComponent(group);
      } else {
        if (this.arenaService.externalComponent) {
          this.destroyExternalComponent();
        }
        this.refreshDataFormDesign(this.nvGroup.indexOf(group));
        this.setDrawer();
        this.setSideNavProperties(!this.abcSideNav.opened);
        this.dataFormComponent?.setDrawerOpened(!this.abcSideNav.opened, this.sideNavIsResized, this.sideNavWidth);

        if (toggle) {
          this.setSideNavProperties(this.abcSideNav.drawer.opened ? !this.abcSideNav.opened : this.abcSideNav.opened);
          await this.abcSideNav?.setOpened(true);
          this.dataFormComponent?.setDrawerOpened(!this.abcSideNav.opened, this.sideNavIsResized, this.sideNavWidth);
        } else if (
          !this.isNavigating &&
          (activateOnly == true ||
            this.currentDataContext?.objectids?.length ||
            this.context.nextDataContext?.objectids?.length ||
            !this.uiconfig.renderNavigation)
        ) {
          await this.abcSideNav?.setOpened(false);
        }
        this.arenaService.externalComponent = false;
      }
      return true;
    }
  }

  private canNavigate(): boolean {
    if (this.dataFormComponent?.hasChanges) {
      return !this.dataFormComponent.hasChanges;
    }
    if (this.currentDataEntity && this.menuComponent?.hasChanges) {
      this.menuComponent?.ensureExpanded();
      return false;
    }

    return true;
  }

  public async openLayoutByQueryParams(queryParams: string): Promise<void> {
    this.location.replaceState(window.location?.pathname, queryParams);
    this.onUrlChanged(this.route.snapshot.url, this.route.snapshot.queryParamMap);
  }

  public collapseDataForm(e: ItemCollapsedEvent) {
    if (e.itemIndex === 0) {
      e.element.style.paddingTop = '24px';
    }
    this.hignaComponent.abcGrid.instance.updateDimensions();
  }

  public expandDataForm(e: ItemExpandedEvent) {
    e.element.style.paddingTop = '0px';
    this.hignaComponent.abcGrid.instance.updateDimensions();
  }

  private documentsByObjectType: Map<
    string,
    {
      type: ArenaBceDocumentType;
      document: DefinitionDocumentReference;
      allowMultiSelect: boolean;
    }[]
  > = new Map();
  private async findAdditionalHigonActions(objectType: string | number): Promise<AdditionalHigonAction[]> {
    const result: AdditionalHigonAction[] = [];
    const additionalHigonActions: AdditionalHigonAction[] = [];
    if (this.abcArenaService.additionalHigonActions) {
      additionalHigonActions.push(...this.abcArenaService.additionalHigonActions);
    }
    additionalHigonActions.push(...(await this.currentLayoutPluginActions.findAdditionalHigonActions()));
    if (additionalHigonActions.length) {
      for (const action of additionalHigonActions) {
        const ot = await this.dataService.abcCore.objectSpace.getObjectType(action.objectType).toPromise();
        if (ot?.isMatchOrBase(objectType)) {
          result.push(action);
        }
      }
    }

    const reports = this.currentProfileInfo?.settings?.findSpecialReportCommandDocuments('Arena_Reporting');
    const queries = this.currentProfileInfo?.settings?.findSpecialQueryCommandDocuments('Arena_Reporting');
    if (reports?.length || queries?.length) {
      const ot = await this.dataService.abcCore.objectSpace.getObjectType(objectType).toPromise();
      if (ot) {
        let documents = this.documentsByObjectType.get(ot.name);
        if (!documents) {
          documents = [];
          const objectTypeNames: string[] = [];
          let current = ot;
          while (current) {
            objectTypeNames.push(current.name);
            current = current.baseType;
          }
          const xpathFilters = this.getXPathFiltersByObjectTypes(objectTypeNames, false);
          const xpathFiltersMultiSelect = this.getXPathFiltersByObjectTypes(objectTypeNames, true);
          const queryIds = await this.bceService.filterDocuments(
            (reports ?? []).concat(queries ?? []).map((_) => _.id),
            xpathFilters.query,
          );
          let multiselectqueryIds: string[];
          if (queryIds?.length) {
            multiselectqueryIds = await this.bceService.filterDocuments(queryIds, xpathFiltersMultiSelect.query);
          }
          let dmlqueryIds: string[];
          let dmlmultiselectqueryIds: string[];
          if (queries?.length) {
            dmlqueryIds = await this.bceService.filterDocuments(
              queries.map((_) => _.id),
              xpathFilters.dmlquery,
            );
            if (dmlqueryIds?.length) {
              dmlmultiselectqueryIds = await this.bceService.filterDocuments(
                dmlqueryIds,
                xpathFiltersMultiSelect.dmlquery,
              );
            }
          }
          if (reports?.length) {
            for (const d of reports) {
              if (queryIds?.includes(d.id)) {
                documents.push({
                  type: ArenaBceDocumentType.Report,
                  document: d,
                  allowMultiSelect: multiselectqueryIds.includes(d.id),
                });
              }
            }
          }
          if (queries?.length) {
            for (const d of queries) {
              if (dmlqueryIds?.includes(d.id)) {
                documents.push({
                  type: ArenaBceDocumentType.DmlQuery,
                  document: d,
                  allowMultiSelect: dmlmultiselectqueryIds.includes(d.id),
                });
              } else if (queryIds?.includes(d.id)) {
                documents.push({
                  type: ArenaBceDocumentType.Query,
                  document: d,
                  allowMultiSelect: multiselectqueryIds.includes(d.id),
                });
              }
            }
          }
          this.documentsByObjectType.set(ot.name, documents);
        }
        for (const d of documents) {
          result.push({
            objectType: objectType,
            imageType: <any>d.type,
            text: d.document.name,
            action: async (action) => {
              const entities = await this.getCurrentOrFilteredDataEntities(d.allowMultiSelect);
              if (entities?.length) {
                await this.executeBceDocument(d.document.id, d.document.name, d.type, entities);
              }
            },
          });
        }
      }
    }
    return result;
  }

  public async getLayout(arenaName: string, layoutId: string): Promise<LayoutViewModel> {
    return await this.dataService.getLayout(arenaName, layoutId);
  }

  protected sumBadgeValues(groups: NavigationGroupViewModel[]): number {
    return NavigationGroupViewModel.sumBadgeValues(groups);
  }
}
